import { useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Search, Grid, List, Map, ChevronDown, ChevronUp, Monitor, Users, Calendar, X, ChevronLeft, ChevronRight, Info, Lock } from "lucide-react";
import { GET_LANDING_EXPLORATION } from "@/Graphql/PropertyListQueries";
import { GET_STATE_LIST_MARKETPLACE, GET_MARKETPLACE_COUNTRIES, COMPANY_LIST, PROPERTY_LIST_VENUE_TYPE_LIST, GET_DISTRICT_LIST_MARKETPLACE } from "@/Graphql/Queries";
import { PACKAGE_BILLBOARD } from "@/Graphql/packageQurie";
import { useToast } from "@/hooks/use-toast";
import { useCart, type CartItem } from '../utils/cartContext';
import BillboardCard from "@/components/billboard-card";
import HeroSection from "@/components/hero-section";
import MapView from "@/components/map-view";
import DealCard from "@/components/DealCard";
import PackageCard from "@/components/package-card";
import { getMediaOwner } from "@/data/media-owners";
import client from "@/Graphql/apolloClient";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useGeolocation } from '@/utils/simpleGeolocation';
import logger from '@/utils/logger';

// Default filter values
const defaultFilters = {
  available: true,
  category: "",
  priceRange: null as [number, number] | null, // Back to null so users must enter values
  venueType: "" as string, // Changed from array to string for radio button
  environment: "" as string,
  targetAgeRange: [] as string[],
  audienceType: [] as string[],
  targetGender: [] as string[],
  isIndoor: null as boolean | null,
  inventoryType: (() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const urlType = urlParams.get('type');
      if (urlType) return urlType;

      const saved = localStorage.getItem('explore-filters');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return parsed.inventoryType || 'digital';
        } catch (e) {
          return 'digital';
        }
      }
    }
    return 'digital';
  })(),
  adPlayMode: [] as string[],
  screenSize: [] as string[],
  billboardFormat: [] as string[],
  orientation: [] as string[],
  mediaOwner: [] as string[],
  country: "" as string,
  state: [] as string[],
  district: [] as string[]
};

const defaultExpandedSections = {
  location: true,
  category: true,
  buyingFormat: true,
  pricing: false,
  valuePacks: false,
  environment: false,
  mediaOwner: false,
  venue: false,
  audience: false,
  inventorySpec: false
};

const formatNumber = (num: number | undefined | null): string => {
  if (!num || typeof num !== 'number') return '0';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  } else {
    return num.toString();
  }
};

interface ExploreProps {
  base_path: string;
  exposure: string;
  configData?: any[];
}

// Utility function to mark continue shopping navigation
export const setContinueShoppingFlag = () => {
  sessionStorage.setItem('continueShopping', 'true');
};

// Make it available globally for cart page to use
if (typeof window !== 'undefined') {
  (window as any).setContinueShoppingFlag = setContinueShoppingFlag;
}

export default function Explore({ base_path, exposure, configData = [] }: ExploreProps) {
  // Add this with other state declarations
  const currentEnvironmentRef = useRef<string>('');
  const previousLogRef = useRef<{ inventoryType: string, totalSize: number, apiTotalSize: number | null, inventoryLength: number } | null>(null);
  const logTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 🔒 Initialization guard state (like start-campaign.tsx)
  const [isCountryInitialized, setIsCountryInitialized] = useState(false);

  // Simple geolocation on page load
  const { result: geoResult } = useGeolocation();

  // DEBUG: Log geolocation result
  useEffect(() => {


    const useTestIP = import.meta.env.VITE_TEST_IP_STATUS === 'true';
    const testIP = import.meta.env.VITE_TEST_IP;

  }, [geoResult]);

  // Country selection handled in landing page only
  // --- Preserve localStorage when navigating back, only clear for new windows ---
  useEffect(() => {
    const initializeSessionFlag = () => {
      // Check if this is a new window/tab by looking for a session-specific flag
      const isNewWindow = !sessionStorage.getItem('windowInitialized');

      if (isNewWindow) {
        // Mark this window as initialized BEFORE clearing anything
        // This prevents clearing filters when navigating back from package details
        sessionStorage.setItem('windowInitialized', 'true');

        // Only clear filters for truly new windows (check if referrer is empty or external)
        const isExternalNavigation = !document.referrer ||
          !document.referrer.includes(window.location.hostname);

        if (isExternalNavigation) {
          // Clear all explore-related localStorage items only for external navigation
          const keysToRemove = [
            'explore-filters',
            'explore-expanded-sections',
            'explore-search-query',
            'exploreCurrentPage',
            'selectedCountryId',
            'countryName'
          ];

          keysToRemove.forEach(key => {
            localStorage.removeItem(key);
          });
        } else {
          logger.info('✅ Window initialized - filters preserved for internal navigation');
        }
      }
    };

    initializeSessionFlag();
  }, []);

  // Log geolocation result
  useEffect(() => {
    if (geoResult) {
      if (geoResult.success) {
        logger.info(`✅ User location: ${geoResult.country} (IP: ${geoResult.ipAddress})`);
      } else {
        logger.warn('⚠️ Geolocation failed:', geoResult.error || geoResult.message);
      }
    }
  }, [geoResult]);

  // --- Media Owner Filter State ---
  // Ensure companyId is declared before use
  const companyId = import.meta.env.VITE_APP_COMPANY_ID;
  logger.info('checking logger working or not');
  // 2-variable country system:
  // - selectedCountryId: Active filter (cart country or user selection)
  // - sessionDefaultCountryId: Session default (IP or landing page selection)
  // Priority: Cart Country > selectedCountryId > sessionDefaultCountryId
  const [selectedCountryId, setSelectedCountryId] = useState<string>(() => {
    // Priority 1: selectedCountryId (active filter or cart country)
    const selected = localStorage.getItem('selectedCountryId');
    if (selected) return selected;

    // Priority 2: sessionDefaultCountryId (session default from landing page)
    const sessionDefault = localStorage.getItem('sessionDefaultCountryId');
    if (sessionDefault) return sessionDefault;

    return '';
  });

  // Use country name for media owner filter (derived from selectedCountryId)
  const countryName = (() => {
    const selected = localStorage.getItem('selectedCountryId');
    if (selected) {
      // Will be resolved to name after countryOptions loads
      return localStorage.getItem('sessionDefaultCountryName') || 'N/A';
    }
    return localStorage.getItem('sessionDefaultCountryName') || 'N/A';
  })();

  // const companyType = "MEDIA_OPERATOR";
  // const accountType = "EXTERNAL";


  const { data: mediaOwnerData, loading: mediaOwnerLoading, error: mediaOwnerError, refetch: refetchMediaOwners } = useQuery(COMPANY_LIST, {
    variables: {
      exposure: exposure,
      countryId: selectedCountryId || '',
      page: 0,  // Assuming starting page is 0
      size: 100,  // Assuming a reasonable size for the list
    },
    skip: false, // Allow loading with default country
    fetchPolicy: 'cache-first',
    onError: (error) => {
      logger.error('Media Owner Filter API Error:', error);
    }
  });

  useEffect(() => {
    if (selectedCountryId) {
      refetchMediaOwners({
        exposure: exposure,
        countryId: selectedCountryId,
        page: 0,
        size: 100,
      });
    }
  }, [selectedCountryId, exposure, refetchMediaOwners]);

  const mediaOwnerOptions = (() => {
    if (mediaOwnerError) {
      logger.error('Error loading media owners:', mediaOwnerError);
      return [];
    }
    if (mediaOwnerLoading) {
      return [];
    }
    if (!mediaOwnerData?.companyList) {
      return [];
    }
    // Sort media owners alphabetically by name before mapping
    return mediaOwnerData.companyList
      .slice() // copy to avoid mutating original
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .map((owner: any) => ({
        label: owner.name,
        value: owner.id,
      }));
  })();

  // Country filter query
  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(GET_MARKETPLACE_COUNTRIES, {
    variables: { exposure: exposure },
    fetchPolicy: 'cache-first',
    onError: (error) => {
      logger.error('Country Filter API Error:', error);
    }
  });

  const countryOptions = (() => {
    if (countryError) {
      logger.error('Error loading countries:', countryError);
      return [];
    }
    if (countryLoading) {
      return [];
    }
    if (!countryData?.getMarketplaceCountries) {
      return [];
    }

    // Sort countries alphabetically by name before mapping
    return countryData.getMarketplaceCountries
      .slice() // copy to avoid mutating original
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .map((country: any) => ({
        label: country.name,
        value: country.id, // Use the id field instead of countryId
        countryId: country.countryId, // Keep countryId for reference
        id: country.id,
        tax: country.tax // Include tax information for cart context
      }));
  })();

  const navigate = useNavigate();

  // Add a function to get the default view mode from config
  const getDefaultViewMode = (): "grid" | "list" | "map" => {
    if (!configData || configData.length === 0) {
      return "grid";
    }

    const searchPageConfig = configData.find((config: any) => config.name.toLowerCase().includes("search page"));
    if (!searchPageConfig?.configurations) return "grid";

    const mapAndListViewConfig = searchPageConfig.configurations.find((config: any) => config.key === "MapAndListView");
    if (!mapAndListViewConfig?.options) return "grid";

    // Directly return the default value as the view mode (now "grid", "list", or "map")
    const defaultView = mapAndListViewConfig.default as "grid" | "list" | "map";
    return ["grid", "list", "map"].includes(defaultView) ? defaultView : "grid";
  };

  const [searchQuery, setSearchQuery] = useState(() => {
    if (typeof window !== 'undefined' && !sessionStorage.getItem('windowInitialized')) return "";
    return localStorage.getItem('explore-search-query') || "";
  });
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid'); // Start with grid as default
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);

  // Update viewMode when configData becomes available
  useEffect(() => {
    if (configData && configData.length > 0) {
      const defaultViewMode = getDefaultViewMode();
      setViewMode(defaultViewMode);
    }
  }, [configData]); // Re-run when configData changes

  const { isAuthenticated } = useAuth();

  const [localPriceRange, setLocalPriceRange] = useState<[number, number]>([0, 0]);

  // Price range input states
  const [minPriceInput, setMinPriceInput] = useState('');
  const [maxPriceInput, setMaxPriceInput] = useState('');

  // Venue types hierarchy state
  const [venueTypesHierarchy, setVenueTypesHierarchy] = useState<any[]>([]);
  const [venueTypeData, setVenueTypeData] = useState<string[]>([]);

  const { toast } = useToast();

  const startDate = moment().format("DD MMMM YYYY");
  const endDate = moment().format("DD MMMM YYYY");
  const [currencyCode, setCurrencyCode] = useState<string>("");

  // Load venue types hierarchy from JSON file
  useEffect(() => {
    const fetchVenueTypes = async () => {
      try {
        const { data } = await client.query({
          query: PROPERTY_LIST_VENUE_TYPE_LIST,
          fetchPolicy: "network-only", // optional: always fetch fresh data
        });

        if (data?.venueTypes) {
          setVenueTypesHierarchy(data.venueTypes);
        } else {
          logger.error("No venue types found in response");
        }
      } catch (err) {
        logger.error("Failed to load venue types hierarchy:", err);
      }
    };

    fetchVenueTypes();
  }, []);

  // Function to get all children and grandchildren for a parent venue type
  const getAllChildrenForVenueType = (parentName: string): string[] => {
    const parent = venueTypesHierarchy.find(item => item.value === parentName);
    if (!parent) return [];

    const allChildren: string[] = [];

    const extractChildren = (item: any) => {
      if (item.child && Array.isArray(item.child)) {
        item.child.forEach((child: any) => {
          allChildren.push(child.value);
          extractChildren(child); // Recursively get grandchildren
        });
      }
    };

    extractChildren(parent);
    return allChildren;
  };

  // Initialize filters from localStorage or defaults
  const [expandedSections, setExpandedSections] = useState(() => {
    // Check if this is a new window
    const isNewWindow = !sessionStorage.getItem('windowInitialized');

    if (typeof window !== 'undefined' && !isNewWindow) {
      const saved = localStorage.getItem('explore-expanded-sections');
      return saved ? JSON.parse(saved) : defaultExpandedSections;
    }
    return defaultExpandedSections;
  });

  const [filters, setFilters] = useState(() => {
    // Check if this is a new window (localStorage was cleared)
    const isNewWindow = !sessionStorage.getItem('windowInitialized');

    // Get default country ID using 2-variable priority
    const defaultCountryId = localStorage.getItem('selectedCountryId') ||
      localStorage.getItem('sessionDefaultCountryId') ||
      '';

    // Check if user is coming from cart
    const fromCart = typeof window !== 'undefined' && (
      new URLSearchParams(window.location.search).get('from') === 'cart' ||
      document.referrer.includes('/cart') ||
      sessionStorage.getItem('continueShopping') === 'true'
    );

    const urlParams = new URLSearchParams(window.location.search);
    const urlType = urlParams.get('type');

    // For new windows, skip loading saved filters and use defaults
    if (typeof window !== 'undefined' && !isNewWindow) {
      const saved = localStorage.getItem('explore-filters');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Force digital inventory, preserve packages only if NOT coming from cart
          return {
            ...defaultFilters,
            ...parsed,
            category: (fromCart || parsed.category !== 'packages') ? '' : 'packages', // Force digital if from cart
            inventoryType: urlType || parsed.inventoryType || 'digital', // URL params take priority
            country: parsed.country || defaultCountryId,
            state: Array.isArray(parsed.state) ? parsed.state : parsed.state ? [parsed.state] : [],
            district: Array.isArray(parsed.district) ? parsed.district : parsed.district ? [parsed.district] : []
          };
        } catch (e) {
          return {
            ...defaultFilters,
            category: '', // Default to empty category
            inventoryType: urlType || 'digital', // URL params take priority
            country: defaultCountryId
          };
        }
      }
    }
    return {
      ...defaultFilters,
      category: '', // Default to empty category
      inventoryType: urlType || 'digital', // URL params take priority
      country: defaultCountryId
    };
  });

  // On mount, detect if coming from cart and force digital, otherwise preserve packages if selected
  useEffect(() => {
    // Check if user is coming from cart page (continue shopping)
    const urlParams = new URLSearchParams(window.location.search);
    const fromCartParam = urlParams.get('from') === 'cart';
    const fromCartReferrer = document.referrer.includes('/cart');
    const fromCartSession = sessionStorage.getItem('continueShopping') === 'true';
    const fromCart = fromCartParam || fromCartReferrer || fromCartSession;

    setFilters((prev: typeof filters) => {
      let newCategory = prev.category;

      if (fromCart) {
        // Force digital when coming from cart (continue shopping)
        newCategory = ''; // Clear category to show digital
        // Clear the session flag
        sessionStorage.removeItem('continueShopping');
      } else {
        // Normal navigation - preserve packages if explicitly selected
        const shouldClearCategory = prev.category !== 'packages';
        newCategory = shouldClearCategory ? '' : prev.category;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const urlType = urlParams.get('type');
      return {
        ...prev,
        category: newCategory, // Digital for cart navigation, preserve packages for normal navigation
        inventoryType: urlType || prev.inventoryType || 'digital', // Preserve URL type or current type
        spotBasedBookingOnly: false,
      };
    });
    // Don't force reset priceFormat - preserve user's selection
    // setTriggerSearch(''); // This will trigger the API call
  }, []);


  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('explore-filters', JSON.stringify(filters));
    }
  }, [filters]);

  useEffect(() => {
    if (filters.environment) {
      currentEnvironmentRef.current = filters.environment;
    }
  }, [filters.environment]);

  // Save expanded sections to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('explore-expanded-sections', JSON.stringify(expandedSections));
    }
  }, [expandedSections]);

  // Save search query to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('explore-search-query', searchQuery);
    }
  }, [searchQuery]);

  // Initialize local price range from filters
  useEffect(() => {
    if (filters.priceRange) {
      setLocalPriceRange(filters.priceRange);
      // Only show values if they are not the default values
      setMinPriceInput(filters.priceRange[0] > 0 ? filters.priceRange[0].toString() : '');
      setMaxPriceInput(filters.priceRange[1] < 999999999 && filters.priceRange[1] !== 50000 ? filters.priceRange[1].toString() : '');
    }
  }, [filters.priceRange]);

  // Handler for price range update on Enter key
  const handlePriceRangeUpdate = () => {
    // Require both min and max values to be entered
    if (minPriceInput === '' || maxPriceInput === '') {
      toast({
        title: "Invalid Price Range",
        description: "Please enter both minimum and maximum price values.",
        variant: "destructive"
      });
      return;
    }

    const minValue = Math.max(0, parseInt(minPriceInput) || 0);
    const maxValue = Math.max(parseInt(maxPriceInput) || 0, minValue);

    if (minValue >= maxValue) {
      toast({
        title: "Invalid Price Range",
        description: "Maximum price must be greater than minimum price.",
        variant: "destructive"
      });
      return;
    }

    const newPriceRange: [number, number] = [minValue, maxValue];
    setLocalPriceRange(newPriceRange);
    setMinPriceInput(minValue.toString());
    setMaxPriceInput(maxValue.toString());
    updateFilter('priceRange', newPriceRange);
  };

  // Handler for min price input changes
  const handleMinPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*$/.test(value)) {
      setMinPriceInput(value);
    }
  };

  // Handler for max price input changes
  const handleMaxPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*$/.test(value)) {
      setMaxPriceInput(value === '' ? '' : value.replace(/^0+(?!$)/, ''));
    }
  };

  // Handler for Enter key press on price inputs
  const handlePriceKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handlePriceRangeUpdate();
    }
  };



  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlType = urlParams.get('type');
    return {
      venueType: [] as string[],
      targetAgeRange: [] as string[],
      audienceType: [] as string[],
      targetGender: [] as string[],
      isIndoor: null as boolean | null,
      inventoryType: urlType || filters.inventoryType || "digital",
      adPlayMode: [] as string[],
      screenSize: [] as string[],
      billboardFormat: [] as string[],
      orientation: [] as string[],
      mediaOwner: [] as string[]
    };
  });

  // Get cart data from CartContext
  const { isInCart, addToCart, removeFromCart, cartItems, updateTaxInfo, cartCountry, setCartCountry, clearCartCountry, hasCartCountry, cartCountryId, setCartCountryId } = useCart();
  const { priceFormat, setPriceFormat } = useCart();
  const [buyingFormat, setBuyingFormat] = useState<'spot-based' | 'day-based'>(priceFormat);
  const [spotBasedBooking, setSpotBasedBooking] = useState<boolean>(priceFormat === 'spot-based');
  const [cartDataLoaded, setCartDataLoaded] = useState(false);


  // Synchronize local UI state with context state
  useEffect(() => {
    setBuyingFormat(priceFormat);
    setSpotBasedBooking(priceFormat === 'spot-based');
  }, [priceFormat]);

  // Early cart data fetch for country initialization
  useEffect(() => {
    // Mark cart data as loaded after checking cart items
    if (cartItems && cartItems.length >= 0) {
      setCartDataLoaded(true);
    }
  }, [cartItems]);
  // (Removed duplicate companyId and countryId declarations)

  // Apollo Client: fetch states and districts
  const { data: stateData, loading: stateLoading, error: stateError, refetch: refetchStates } = useQuery(GET_STATE_LIST_MARKETPLACE, {
    variables: { countryIds: selectedCountryId || localStorage.getItem('sessionDefaultCountryId') || '', exposure: exposure },
    skip: false, // Allow loading with default country
    onCompleted: (data) => {
      logger.info('✅ State query completed:', data);
    },
    onError: (error) => {
      logger.error('❌ State query error:', error);
    }
  });

  const [getDistricts, { data: districtData, loading: districtLoading, error: _districtError }] = useLazyQuery(GET_DISTRICT_LIST_MARKETPLACE);
  const [availableStates, setAvailableStates] = useState<any[]>([]); // [{stateId, name}]
  const [availableDistricts, setAvailableDistricts] = useState<any[]>([]); // [{districtId, name}]

  // Multi-select dropdown states
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);

  // Search input states for dropdowns
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [districtSearchTerm, setDistrictSearchTerm] = useState('');
  const [mediaOwnerSearchTerm, setMediaOwnerSearchTerm] = useState('');
  const [showMediaOwnerDropdown, setShowMediaOwnerDropdown] = useState(false);

  // Populate availableStates when stateData changes
  useEffect(() => {

    if (stateData && (stateData.stateListMarketplace || stateData.stateListMarketplace)) {
      const list = stateData.stateListMarketplace || stateData.stateListMarketplace;
      setAvailableStates(list);
    } else {
      logger.info('❌ state---->  No state data found');
    }
  }, [stateData, stateLoading, stateError]);

  // Populate availableDistricts when districtData changes
  useEffect(() => {
    if (districtData && districtData.districtListMarketplace) {
      const districts = districtData.districtListMarketplace;
      setAvailableDistricts(districts);
    } else if (districtData && !districtData.districtListMarketplace) {
      setAvailableDistricts([]);
    } else {
      logger.info('🏙️ No district data at all');
    }
  }, [districtData]);

  // Load districts for selected state
  useEffect(() => {
    if (filters.state && filters.state.length > 0) {
      // Load districts for the selected state(s) as comma-separated string
      getDistricts({
        variables: { stateIds: filters.state.join(","), exposure: exposure },
        fetchPolicy: 'no-cache' // ✅ Force fresh data to prevent caching issues
      });
    } else {
      setAvailableDistricts([]);
    }
  }, [filters.state, getDistricts]);

  // Helper functions to filter dropdown options based on search term
  const filteredCountryOptions = countryOptions.filter((country: any) =>
    country.label.toLowerCase().includes(countrySearchTerm.toLowerCase())
  );

  const filteredStateOptions = availableStates.filter((state: any) => {
    const stateName = state.name || state.stateName;
    return stateName.toLowerCase().includes(stateSearchTerm.toLowerCase());
  });

  const filteredDistrictOptions = availableDistricts.filter((district: any) => {
    const districtName = district.name || district.districtName;
    return districtName.toLowerCase().includes(districtSearchTerm.toLowerCase());
  });

  const filteredMediaOwnerOptions = mediaOwnerOptions.filter((owner: any) =>
    owner.label.toLowerCase().includes(mediaOwnerSearchTerm.toLowerCase())
  );

  // 🔒 UNIFIED COUNTRY INITIALIZATION (like start-campaign.tsx)
  useEffect(() => {
    // Check if cart conflict was resolved - force re-initialization
    const forceReinit = localStorage.getItem('forceCountryReinitialization');
    if (forceReinit === 'true') {
      setIsCountryInitialized(false);
      localStorage.removeItem('forceCountryReinitialization');
    }

    // Skip if already initialized
    if (isCountryInitialized) {
      logger.info("✅ Country already initialized, skipping");
      return;
    }
    if (countryOptions.length === 0) {
      logger.info("⏳ Waiting for countryOptions to load");
      return;
    }

    // Wait until cart data is loaded
    if (!cartDataLoaded) {
      logger.info("⏳ Waiting for cart data to load before country initialization");
      return;
    }

    // Navigation backup restoration
    const navigationBackup = localStorage.getItem('navigationCountryBackup');
    if (navigationBackup) {
      try {
        const backup = JSON.parse(navigationBackup);
        if (backup.timestamp > Date.now() - 3600000) { // 1 hour
          const backupCountry = countryOptions.find((c: any) => c.id === backup.countryId);
          if (backupCountry) {
            setSelectedCountryId(backupCountry.value);
            if (filters.country !== backupCountry.value) {
              setFilters((prev: any) => ({ ...prev, country: backupCountry.value }));
            }
            if (backupCountry.tax) {
              updateTaxInfo(backupCountry.tax.label || 'Tax', backupCountry.tax.percent || 0);
            }
            setIsCountryInitialized(true);
            localStorage.removeItem('navigationCountryBackup');
            return;
          }
        } else {
          localStorage.removeItem('navigationCountryBackup');
        }
      } catch {
        localStorage.removeItem('navigationCountryBackup');
      }
    }

    const storedSelectedCountryId = localStorage.getItem('selectedCountryId');
    const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');

    let chosen: any = null;
    let source = 'none';

    // Priority 1: Saved selectedCountryId (user's explicit selection - HIGHEST PRIORITY for filter retention)
    if (storedSelectedCountryId) {
      chosen = countryOptions.find((c: any) => c.id === storedSelectedCountryId);
      if (chosen) {
        source = 'selectedCountryId';
      }
    }

    // Priority 2: Cart country (if cart has items and no saved selection)
    if (!chosen && cartCountry && hasCartCountry()) {
      const lowered = cartCountry.toLowerCase();
      chosen = countryOptions.find((c: any) =>
        c.label.toLowerCase() === lowered ||
        c.countryId.toLowerCase() === lowered ||
        (c.iso && c.iso.toLowerCase() === lowered)
      );
      if (chosen) {
        source = 'cart';
      }
    }

    // Priority 3: sessionDefaultCountryId (IP/landing page default)
    if (!chosen && sessionDefaultCountryId) {
      chosen = countryOptions.find((c: any) => c.id === sessionDefaultCountryId);
      if (chosen) {
        source = 'sessionDefault';
      }
    }

    // Priority 4: First available country
    if (!chosen && countryOptions.length > 0) {
      chosen = countryOptions[0];
      source = 'firstAvailable';
    }

    if (!chosen) {
      logger.info('❌ No country available for initialization');
      return;
    }

    // Set the country
    setSelectedCountryId(chosen.value);
    if (filters.country !== chosen.value) {
      setFilters((prev: any) => ({ ...prev, country: chosen.value }));
    }
    localStorage.setItem('selectedCountryId', chosen.id);
    if (chosen.tax) {
      updateTaxInfo(chosen.tax.label || 'Tax', chosen.tax.percent || 0);
    }

    // Mark as initialized BEFORE triggering search to prevent double calls
    setIsCountryInitialized(true);
    hasPerformedInitialSearch.current = false; // Reset search flag for initial search
  }, [countryOptions.length, cartDataLoaded, cartCountry, hasCartCountry, filters.country, updateTaxInfo]);

  // Debug: log final initialization state once selectedCountryId stabilizes
  useEffect(() => {
    if (!isCountryInitialized) return;
  }, [selectedCountryId]);

  // Trigger initial search only once after country initialization
  useEffect(() => {
    // Only trigger on first successful country initialization
    if (!isCountryInitialized || hasPerformedInitialSearch.current) {
      return;
    }

    if (!selectedCountryId) {
      return;
    }
    hasPerformedInitialSearch.current = true;

    // Empty string will trigger search with current filters
    setTriggerSearch('');
  }, [selectedCountryId, isCountryInitialized]);

  // Late cart sync: if cart items appear after init and differ from selectedCountryId, switch
  useEffect(() => {
    if (!isCountryInitialized) return; // only act after initial selection
    if (countryOptions.length === 0) return;
    if (!cartCountry) return;

    const lowered = cartCountry.toLowerCase();
    const cartOption = countryOptions.find((c: any) =>
      c.label.toLowerCase() === lowered ||
      c.countryId.toLowerCase() === lowered ||
      (c.iso && c.iso.toLowerCase() === lowered)
    );
    if (!cartOption) return;

    const cartHasItems = cartItems.length > 0;
    if (cartHasItems && selectedCountryId !== cartOption.value) {
      setSelectedCountryId(cartOption.value);
      setFilters((prev: any) => ({ ...prev, country: cartOption.value }));
      if (cartOption.tax) {
        updateTaxInfo(cartOption.tax.label || 'Tax', cartOption.tax.percent || 0);
      }
    }
  }, [cartItems.length, selectedCountryId, cartCountry, countryOptions.length, updateTaxInfo]);

  // Enhanced navigation protection - prevent country resets during navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Preserve user's manual country selection across navigation
      if (selectedCountryId) {
        const selectedCountry = countryOptions.find((c: any) => c.value === selectedCountryId);
        if (selectedCountry) {
          localStorage.setItem('selectedCountryId', selectedCountry.id);
          localStorage.setItem('navigationCountryBackup', JSON.stringify({
            countryId: selectedCountry.id,
            countryValue: selectedCountry.value,
            countryLabel: selectedCountry.label,
            timestamp: Date.now()
          }));
        }
      }
    };

    const handleVisibilityChange = () => {
      // Also preserve on visibility change (covers more navigation scenarios)
      if (document.visibilityState === 'hidden' && selectedCountryId) {
        const selectedCountry = countryOptions.find((c: any) => c.value === selectedCountryId);
        if (selectedCountry) {
          localStorage.setItem('navigationCountryBackup', JSON.stringify({
            countryId: selectedCountry.id,
            countryValue: selectedCountry.value,
            countryLabel: selectedCountry.label,
            timestamp: Date.now()
          }));
        }
      }
    };

    const handleFocus = () => {
      // When page regains focus, check for navigation backup
      const navigationBackup = localStorage.getItem('navigationCountryBackup');

      if (navigationBackup && countryOptions.length > 0) {
        try {
          const backup = JSON.parse(navigationBackup);
          // Use backup if it's recent (within 1 hour)
          if (backup.timestamp > Date.now() - 3600000) {
            const backupCountry = countryOptions.find((c: any) => c.id === backup.countryId);
            if (backupCountry && selectedCountryId !== backupCountry.value) {
              setSelectedCountryId(backupCountry.value);
              setFilters((prev: any) => ({
                ...prev,
                country: backupCountry.value
              }));
              setIsCountryInitialized(true);
            }
          }
        } catch (e) {
          logger.error("Error parsing navigation backup on focus:", e);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedCountryId, countryOptions]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Close all dropdowns if click is outside any dropdown container
      if (!target.closest('[data-dropdown="country"]')) {
        setShowCountryDropdown(false);
      }
      if (!target.closest('[data-dropdown="state"]')) {
        setShowStateDropdown(false);
      }
      if (!target.closest('[data-dropdown="district"]')) {
        setShowDistrictDropdown(false);
      }
      if (!target.closest('[data-dropdown="mediaOwner"]')) {
        setShowMediaOwnerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Pagination state - moved before query to use in variables
  const [currentPage, setCurrentPage] = useState(() => {
    // Check if this is a new window (logic matches your existing restore effect)
    const isNewWindow = typeof window !== 'undefined' && !sessionStorage.getItem('windowInitialized');

    if (typeof window !== 'undefined' && !isNewWindow) {
      const savedPage = localStorage.getItem('exploreCurrentPage');
      if (savedPage) {
        const pageNum = parseInt(savedPage, 10);
        if (!isNaN(pageNum) && pageNum >= 0) {
          return pageNum;
        }
      }
    }
    return 0;
  });

  // Add this ref to track initial mount
  const isFirstRender = useRef(true);
  const hasPerformedInitialSearch = useRef(false);
  const previousFiltersRef = useRef<typeof filters | null>(null);
  const shouldResetPageRef = useRef(false);
  const [triggerSearch, setTriggerSearch] = useState<string>(() => {
    if (typeof window !== 'undefined' && !sessionStorage.getItem('windowInitialized')) return "";
    return localStorage.getItem('explore-search-query') || "";
  });
  const [isSearching, setIsSearching] = useState(false);

  // Handle restoration of page from from_page parameter (coming back from details)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const fromPageParam = urlParams.get('from_page');

      if (fromPageParam) {
        const fromPageNum = parseInt(fromPageParam, 10);
        if (!isNaN(fromPageNum) && fromPageNum >= 0) {
          setCurrentPage(fromPageNum);
          localStorage.setItem('exploreCurrentPage', String(fromPageNum));

          // Clean up the URL by removing from_page parameter
          const newParams = new URLSearchParams(window.location.search);
          newParams.delete('from_page');
          window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
        }
      }
    }
  }, []);

  // Package-related state
  const [packageData, setPackageData] = useState<any[]>([]);
  const [packageDataLoading, setPackageDataLoading] = useState(false);
  const [noDataMessage, setNoDataMessage] = useState<string | null>(null);
  // Featured billboards - separate from search results, always show first 4 from initial load
  const [featuredBillboards, setFeaturedBillboards] = useState<any[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);

  const itemsPerPage = 12; // Changed to 12 for better grid layout (4x3)

  // Use lazy query for search functionality similar to BillboardFilters
  const [executeSearch, { data, loading, error }] = useLazyQuery(GET_LANDING_EXPLORATION, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      logger.info('✅ GraphQL query completed successfully:', data);
    },
    onError: (error) => {
      logger.error('❌ GraphQL query error:', error);
    }
  });

  // Package query for when packages category is selected
  const [executePackageSearch, { data: packageDataResult, loading: packageLoading, error: packageError }] = useLazyQuery(PACKAGE_BILLBOARD, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      logger.info('✅ Package GraphQL query completed successfully:', data);
    },
    onError: (error) => {
      logger.error('❌ Package GraphQL query error:', error);
    }
  });

  // Combined loading state similar to start-campaign.tsx
  const isPageLoading = (filters.category === 'packages' ? packageDataLoading : loading) || isSearching || stateLoading || districtLoading;

  // Log any GraphQL errors
  useEffect(() => {
    if (error) {
      logger.error('🚨 GraphQL Error Details:', error);
      logger.error('🚨 Error Message:', error.message);
      logger.error('🚨 Network Error:', error.networkError);
      logger.error('🚨 GraphQL Errors:', error.graphQLErrors);
    }
  }, [error]);

  // Handle package query response
  useEffect(() => {
    if (packageError) {
      logger.error('🚨 Package GraphQL Error Details:', packageError);
      setPackageData([]);
      setFeaturedBillboards([]);
      setPackageDataLoading(false);
    }
  }, [packageError]);

  // Check if packages category is selected on mount and fetch package data
  useEffect(() => {
    if (filters.category === 'packages') {
      fetchPackageData(selectedCountryId, triggerSearch || searchQuery);
    }
  }, [filters.category, selectedCountryId, triggerSearch]);

  // Initialize search on component mount and when filters change
  useEffect(() => {
    // Guard: Only run search if country is initialized and selectedCountryId is valid
    if (!isCountryInitialized || !selectedCountryId) {
      logger.info('⏳ Waiting for country initialization before API call');
      return;
    }
    const performSearch = async () => {
      // Skip search if packages category is selected - use fetchPackageData instead
      if (filters.category === 'packages') {
        return;
      }
      setIsSearching(true);
      try {
        // Use selected state and district for API call, send empty string if not selected
        const stateId = filters.state.length > 0 ? filters.state.join(",") : "";
        const districtId = filters.district.length > 0 ? filters.district.join(",") : "";
        // companyId for search: comma-separated string of selected media owner IDs, or empty string
        const selectedCompanyId = filters.mediaOwner && filters.mediaOwner.length > 0 ? filters.mediaOwner.join(",") : "";
        if (filters.priceRange) {
          logger.info('💰 minPrice:', filters.priceRange[0], 'maxPrice:', filters.priceRange[1]);
        } else {
          logger.info('❌ No price range set in filters');
        }
        // Use selectedCountryId directly since we've already initialized
        // Prefer localStorage to handle race conditions during re-initialization
        const finalCountryId = localStorage.getItem('selectedCountryId') || selectedCountryId;
        const variables: any = {
          billboard: filters.inventoryType,
          page: currentPage,
          size: itemsPerPage,
          sort: "last_modified_date,desc",
          startDate: startDate,
          endDate: endDate,
          dspName: exposure,
          searchTerm: triggerSearch || '',
          companyId: selectedCompanyId,
          countryId: finalCountryId,
          stateId: stateId,
          districtId: districtId,
          isCurated: true,
          filterData: {
            availableBooking: filters.available,
            categories: (() => {
              const categories = filters.category ? [filters.category] : [];
              // Add environment to categories if selected
              const currentEnvironment = currentEnvironmentRef.current;
              if (currentEnvironment && currentEnvironment !== '') {
                categories.push(currentEnvironment.toUpperCase());
              }
              return categories;
            })(),
            format: filters.billboardFormat || [],
            venueType: filters.venueType || [],
            type: (filters.inventoryType).toUpperCase(),
            // You can add more fields as needed
            // Do not send mediaOwners in filterData for search
          }
        };
        // Only include price parameters if user has entered price values
        if (filters.priceRange && filters.priceRange[0] > 0 && filters.priceRange[1] < 99999999) {
          variables.minPrice = filters.priceRange[0];
          variables.maxPrice = filters.priceRange[1];
        } else {
          logger.info('💰 Not sending price parameters - using server defaults');
        }
        // Add buyingFormat to variables
        if (filters.inventoryType === 'digital') {
          variables.spotBasedBookingOnly = spotBasedBooking;
        }
        const result = await executeSearch({
          variables
        });
        if (result?.data) {
          const inventory = result.data.landingpageExplorePropertiesNotoken || [];
          setInventory(inventory);
        } else {
          setInventory([]);
        }
      } catch (err) {
        logger.error('Search error:', err);
      } finally {
        setIsSearching(false);
      }
    };
    performSearch();
  }, [executeSearch, filters, triggerSearch, currentPage, spotBasedBooking, selectedCountryId, isCountryInitialized]);

  // Trigger search when triggerSearch changes (only on Enter key or filter changes)
  useEffect(() => {
    if (triggerSearch !== searchQuery && triggerSearch !== '') {
      // This will trigger the main search effect above
      return;
    }
  }, [triggerSearch]);

  // Pagination - Use API response pagination data
  // const inventory = data?.landingpageExplorePropertiesNotoken || [];

  // Use packageData when packages category is selected, otherwise use regular inventory
  const paginatedInventory = filters.category === 'packages'
    ? packageData
    : inventory;


  // Helper function to get currency code from inventory data
  const getCurrentCurrencyCode = () => {
    // First try featuredBillboards (for HeroSection), then paginatedInventory
    const dataSource = featuredBillboards.length > 0 ? featuredBillboards : paginatedInventory;
    if (dataSource.length > 0) {
      const currencies = dataSource[0]?.price?.localCurrencies;
      if (currencies && currencies.code) {
        return currencies.code;
      }
    }
    return currencyCode; // fallback to current state
  };

  // Helper function to get currency symbol from inventory data
  const getCurrentCurrencySymbol = () => {
    // First try featuredBillboards (for HeroSection), then paginatedInventory
    const dataSource = featuredBillboards.length > 0 ? featuredBillboards : paginatedInventory;
    if (dataSource.length > 0) {
      const currencies = dataSource[0]?.price?.localCurrencies;
      if (currencies && currencies.symbol) {
        return currencies.symbol;
      }
    }
    return '$'; // fallback
  };

  // Package data fetching function (similar to start-campaign.tsx but without access token)
  const fetchPackageData = async (countryId?: string, searchValue?: string) => {
    // Prevent multiple simultaneous calls
    if (packageDataLoading) {
      return;
    }

    const effectiveCountryId = countryId || selectedCountryId;
    if (!effectiveCountryId) {
      return;
    }

    const stateIdString = filters.state.length > 0 ? filters.state.join(',') : '';
    if (!stateIdString && filters.district.length > 0) {
      setNoDataMessage('Please select at least one state to load packages.');
      return;
    }

    try {
      setPackageDataLoading(true);
      setNoDataMessage(null);

      // Date calculation: startDate = month start, endDate = month end, packageDate = today
      const monthStart = moment().startOf('month').format('YYYY-MM-DD');
      const monthEnd = moment().endOf('month').format('YYYY-MM-DD');
      const today = moment().format('YYYY-MM-DD');

      const variables: any = {
        // No access token for explore page (public access) - but still include as null
        accessToken: null,
        page: currentPage,
        size: 12,
        sort: "last_modified_date,desc",
        startDate: monthStart,
        endDate: monthEnd,
        exposure: exposure || '',
        packageDate: today,
        packageType: "DAYWISE",
        publisherId: filters.mediaOwner && filters.mediaOwner.length > 0
          ? filters.mediaOwner.join(',')
          : "", // Empty to fetch all publishers
        countryId: effectiveCountryId,
        searchTerm: searchValue || '', // Add search term parameter
      };

      const result = await executePackageSearch({
        variables,
        fetchPolicy: 'no-cache'
      });

      if (result?.data) {
        const packages = result.data.packageBillboard || [];

        if (packages.length === 0) {
          setNoDataMessage(searchValue ? 'No Packages Found for Your Search.' : 'No Packages Available.');
          setPackageData([]);
          setFeaturedBillboards([]);
        } else {
          setPackageData(packages);
          // Only set featured on page 0
          if (currentPage === 0) {
            setFeaturedBillboards(packages.slice(0, 4));
          }
          setNoDataMessage(null);
        }
      } else {
        setNoDataMessage(searchValue ? 'No Packages Found for Your Search.' : 'No Packages Available.');
        setPackageData([]);
        setFeaturedBillboards([]);
      }
    } catch (error) {
      logger.error("Error in fetchPackageData:", error);
      setNoDataMessage('Failed to load packages.');
      setPackageData([]);
      setFeaturedBillboards([]);
    } finally {
      setPackageDataLoading(false);
    }
  };



  // Update featured billboards whenever inventory or filters change
  useEffect(() => {
    // Immediately clear featured billboards when filters change, causing loading state to show
    if (isPageLoading) {
      setFeaturedBillboards([]);
      return;
    }

    // Update featured billboards based on current filter selection
    if (inventory.length > 0 && filters.category !== 'packages') {
      setFeaturedBillboards(inventory.slice(0, 4));
      setNoDataMessage(null);
    } else if (filters.category === 'packages' && packageData.length > 0) {
      setFeaturedBillboards(packageData.slice(0, 4));
      setNoDataMessage(null);
    } else {
      setFeaturedBillboards([]);
      // Only set noDataMessage if we're not loading and have no data
      if (!isPageLoading) {
        setNoDataMessage(filters.category === 'packages' ? 'No Packages Available.' : 'No inventory found');
      }
    }
  }, [inventory, filters, packageData, isPageLoading]);

  // Set featured billboards for packages when packages category is selected
  useEffect(() => {
    if (filters.category === 'packages' && packageData.length > 0 && featuredBillboards.length === 0) {
      setFeaturedBillboards(packageData.slice(0, 4));
    }

    const dataSource = featuredBillboards.length > 0 ? featuredBillboards : paginatedInventory;

    if (dataSource.length > 0) {
      const currencies = dataSource[0]?.price?.localCurrencies;

      if (currencies && currencies.code) {
        setCurrencyCode(currencies.code);
      } else {
        setCurrencyCode(dataSource[0]?.currencyCode)
      }
    } else {
      logger.info('💱 No data available, keeping current currency:', currencyCode);
    }
  }, [featuredBillboards, paginatedInventory, filters.country]);
  if (featuredBillboards.length > 0) {
    logger.info('🎯 First featured billboard price:', featuredBillboards[0]?.price);
  }

  const isPackageCategory = filters.category === 'packages';
  const currentDataSource = isPackageCategory ? packageData : inventory;
  const apiResponseData = data?.landingpageExplorePropertiesNotoken || [];
  const packageResponseData = packageDataResult?.packageBillboard || [];

  // Get totalPages directly from API response (inside first item)
  const apiTotalPages = isPackageCategory
    ? (packageResponseData.length > 0 ? packageResponseData[0]?.totalPages : null)
    : (apiResponseData.length > 0 ? apiResponseData[0]?.totalPages : null);

  // Get totalSize/totalPackages for display purposes
  const apiTotalSize = isPackageCategory
    ? (packageResponseData.length > 0 ? (packageResponseData[0]?.totalPackages || packageResponseData[0]?.totalSize) : null)
    : (apiResponseData.length > 0 ? apiResponseData[0]?.totalSize : null);

  const totalDataLength = isPackageCategory
    ? (apiTotalSize || packageData.length)
    : (apiTotalSize || inventory.length);

  // Use API totalPages if available, otherwise calculate from totalDataLength
  const totalPages = apiTotalPages ?? Math.ceil(totalDataLength / itemsPerPage);

  // Debug pagination values
  logger.info('📊 Pagination Debug:', {
    isPackageCategory,
    apiTotalPages,
    apiTotalSize,
    totalDataLength,
    itemsPerPage,
    totalPages,
    currentPage,
    packageDataLength: packageData.length,
    inventoryLength: inventory.length
  });

  const pageStartData = currentDataSource.length > 0 ? Math.min(currentPage * itemsPerPage + 1, totalDataLength) : 0;
  const pageEndData = currentDataSource.length > 0 ? Math.min((currentPage + 1) * itemsPerPage, (currentPage * itemsPerPage) + currentDataSource.length) : 0;

  const hasNextPage = currentPage < totalPages - 1;

  useEffect(() => {
    const inventoryType = filters.category === 'packages' ? 'packages' : filters.inventoryType;
    const apiResponseData = data?.landingpageExplorePropertiesNotoken || [];
    const apiTotalSize = apiResponseData.length > 0 ? apiResponseData[0]?.totalSize : null;
    const totalSize = filters.category === 'packages'
      ? packageData.length
      : (apiTotalSize || inventory.length);

    const shouldLog = totalSize > 0 && (
      !previousLogRef.current ||
      previousLogRef.current.inventoryType !== inventoryType ||
      previousLogRef.current.totalSize !== totalSize
    );

    if (shouldLog) {
      previousLogRef.current = { inventoryType, totalSize, apiTotalSize, inventoryLength: inventory.length };
    }
  }, [filters.category, filters.inventoryType, totalDataLength, currentPage]);

  // Handle apply filters function similar to BillboardFilters
  const handleApplyFilters = async (filterData: any) => {
    setIsSearching(true);
    setCurrentPage(0); // Reset to first page when applying filters

    try {
      // Use selected state and district for API call, send empty string if not selected
      const stateId = filterData.state || "";
      const districtId = filterData.district || "";

      // companyId for search: comma-separated string of selected media owner IDs, or empty string
      const selectedCompanyId = filterData.mediaOwner && filterData.mediaOwner.length > 0 ? filterData.mediaOwner.join(",") : "";

      if (filterData.priceRange) {
        logger.info('💰 Filter minPrice:', filterData.priceRange[0], 'maxPrice:', filterData.priceRange[1]);
      }

      // Prepare variables object - include all parameters for debugging  
      const variables: any = {
        billboard: filterData.inventoryType || filters.inventoryType,
        page: 0,
        size: itemsPerPage,
        sort: "last_modified_date,desc",
        startDate: startDate,
        endDate: endDate,
        dspName: exposure,
        searchTerm: triggerSearch || '',
        companyId: selectedCompanyId,
        countryId: selectedCountryId || '',
        stateId: stateId,
        districtId: districtId,
        isCurated: true,
        filterData: {
          availableBooking: filterData.available,
          categories: filterData.category ? [filterData.category] : [],
          format: filterData.billboardFormat || [],
          venueType: filterData.venueType || [],
          type: (filterData.inventoryType).toUpperCase(),
          // Remove price from filterData as it should be at top level
          // Do not send mediaOwners in filterData for search
        }
      };

      // Only include price parameters if user has entered price values
      if (filterData.priceRange && filterData.priceRange[0] > 0 && filterData.priceRange[1] < 99999999) {
        variables.minPrice = filterData.priceRange[0];
        variables.maxPrice = filterData.priceRange[1];
      } else {
        logger.info('💰 Filter not sending price parameters - using server defaults');
      }

      await executeSearch({
        variables
      });
    } catch (err) {
      logger.error('Filter search error:', err);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search function
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // Save search query to localStorage
    localStorage.setItem('explore-search-query', query);
  };

  // Handle search reset
  const handleResetSearch = () => {
    setSearchQuery('');
    setTriggerSearch('');
    setCurrentPage(0);

    // 🔴 FIX ADDED HERE: Explicitly reset the saved page in local storage
    localStorage.setItem('exploreCurrentPage', '0');

    // Check if current category is packages
    if (filters.category === 'packages') {
      fetchPackageData(selectedCountryId, '');
    }

    // Clear from localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('explore-search-query');
    }
  };

  // Pagination functions following BillboardListing pattern (API-based)
  const paginationPlus = useCallback(() => {
    // For API pagination, we check if current page has full results
    if (!hasNextPage) {
      toast({
        title: "Last page",
        description: "You are already on the last page.",
        variant: "destructive"
      });
      return;
    }
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    localStorage.setItem('exploreCurrentPage', newPage.toString());
    scrollToTop();
  }, [currentPage, hasNextPage, toast]);

  const paginationMinus = useCallback(() => {
    if (currentPage === 0) {
      toast({
        title: "First page",
        description: "You are already on the first page.",
        variant: "destructive"
      });
      return;
    }
    const newPage = currentPage - 1;
    setCurrentPage(newPage);
    localStorage.setItem('exploreCurrentPage', newPage.toString());
    scrollToTop();
  }, [currentPage, toast]);

  const handlePageClick = useCallback((page: number) => {
    if (page === currentPage) return;
    setCurrentPage(page);
    localStorage.setItem('exploreCurrentPage', page.toString());
    scrollToTop();
  }, [currentPage]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  // Reset pagination when filters change (use 0-based indexing)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousFiltersRef.current = filters;
      return;
    }

    // Compare current filters with previous filters to detect intentional changes
    const filtersChanged = JSON.stringify(previousFiltersRef.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setCurrentPage(0);
      localStorage.setItem('exploreCurrentPage', '0');
      shouldResetPageRef.current = true;
    } else {
      logger.info('✅ No filter changes - Keeping current page');
    }

    // Update previous filters for next comparison
    previousFiltersRef.current = filters;
  }, [filters]);

  // Trigger package API call when currentPage changes
  useEffect(() => {
    if (filters.category === 'packages' && selectedCountryId) {
      fetchPackageData(selectedCountryId, triggerSearch || searchQuery);
    }
  }, [currentPage, filters.category]);

  // // Restore page from localStorage on component mount
  // useEffect(() => {
  //   // Only restore page if this is not a new window
  //   const isNewWindow = !sessionStorage.getItem('windowInitialized');

  //   if (typeof window !== 'undefined' && !isNewWindow) {
  //     const savedPage = localStorage.getItem('exploreCurrentPage');
  //     if (savedPage) {
  //       const pageNum = parseInt(savedPage, 10);
  //       if (!isNaN(pageNum) && pageNum >= 0) {
  //         setCurrentPage(pageNum);
  //       }
  //     }
  //   }
  // }, []);

  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(advancedFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        count += value.length;
      } else if (value && value !== "" && value !== null) {
        count += 1;
      }
    });
    // Add basic filters count
    if (filters.state && filters.state.length > 0) count += filters.state.length;
    if (filters.district && filters.district.length > 0) count += filters.district.length;
    if (filters.category && filters.category !== "") count++;
    return count;
  };

  const handleApplyAdvancedFilters = () => {
    setIsAdvancedFiltersOpen(false);
    // Filters are already applied via the query dependencies
  };

  const handleClearAdvancedFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlType = urlParams.get('type');
    setAdvancedFilters({
      venueType: [],
      targetAgeRange: [],
      audienceType: [],
      targetGender: [],
      isIndoor: null,
      inventoryType: urlType || "digital",
      adPlayMode: [],
      screenSize: [],
      billboardFormat: [],
      orientation: [],
      mediaOwner: []
    });
    setFilters({
      state: [] as string[],
      district: [] as string[],
      available: true,
      category: "",
      venueType: [],
      targetAgeRange: [],
      audienceType: [],
      targetGender: [],
      isIndoor: null,
      inventoryType: (() => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlType = urlParams.get('type');
        return urlType || 'digital';
      })(),
      adPlayMode: [],
      screenSize: [],
      billboardFormat: [],
      orientation: [],
      mediaOwner: [],
    });
    setAvailableDistricts([]); // Clear districts when clearing filters
  };

  const handleInventorySelect = (inventoryItem: any) => {
    const billboardType = inventoryItem.registryPackageType || inventoryItem.inventoryType || inventoryItem.type || 'digital';
    
    // Determine buying format - only for digital billboards
    const getBuyingFormat = (): string | null => {
      if (billboardType?.toLowerCase() === 'digital') {
        if (priceFormat === 'spot-based') {
          return 'spot';
        }
        return 'day';
      }
      return null; // No buying format for classic billboards or packages
    };
    
    const buyingFormat = getBuyingFormat();
    
    if (filters.category === 'deals' || inventoryItem.discountedPrice || inventoryItem.originalPrice) {
      const dealUrl = buyingFormat 
        ? `/deal/${inventoryItem.id}?from_page=${currentPage}&buyingFormat=${buyingFormat}`
        : `/deal/${inventoryItem.id}?from_page=${currentPage}`;
      navigate(dealUrl);
    } else if (filters.category === 'packages' || inventoryItem?.registryPackageType === 'DAYWISE') {
      navigate(`${base_path}/package/${inventoryItem.id}?type=${encodeURIComponent(billboardType)}&from_page=${currentPage}`, {
        state: {
          currentPage: currentPage,
        }
      });
    } else {
      if (selectedCountryId) {
        const filterCountryObj = countryOptions.find((c: any) => c.id === filters.country);
        if (filterCountryObj) {
          // ✅ ADD: Update tax info for saved filters
          if (filterCountryObj.tax) {
            const taxLabel = filterCountryObj.tax.label || 'Tax';
            const taxPercent = filterCountryObj.tax.percent || 0;
            updateTaxInfo(taxLabel, taxPercent);
          } else {
            updateTaxInfo('Tax', 0);
          }
        }
      }

      const currentPrice =
        inventoryItem.type === 'DIGITAL'
          ? priceFormat === 'spot-based'
            ? inventoryItem.perSpotPrice
            : inventoryItem.perDayPrice
          : inventoryItem?.sellingRate?.monthly?.month1 > 0
            ? inventoryItem.sellingRate.monthly.month1
            : inventoryItem.currentPrice;

      const currency = inventoryItem?.price?.localCurrencies
        ? inventoryItem?.price?.localCurrencies?.code
        : inventoryItem?.currencyCode;

      // Get the billboard type from the inventory item
      const billboardUrl = buyingFormat
        ? `${base_path}/billboard/${inventoryItem.id}?type=${encodeURIComponent(billboardType)}&from_page=${currentPage}&buyingFormat=${buyingFormat}`
        : `${base_path}/billboard/${inventoryItem.id}?type=${encodeURIComponent(billboardType)}&from_page=${currentPage}`;
      
      navigate(billboardUrl, {
        state: {
          currentPrice: currentPrice,
          priceFormat: priceFormat,
          currencyCode: currency || 'N/A',
          currentPage: currentPage,
          buyingFormat: buyingFormat,
          billboardType: billboardType,
          ...(buyingFormat && { buyingFormat: buyingFormat }),
        }
      });
    }
  };

  const handleAddToCart = (billboard: any) => {
    let price = 0;
    if (billboard.type === 'DIGITAL') {
      price = priceFormat === 'spot-based'
        ? billboard.perSpotPrice
        : billboard.perDayPrice;
    } else {
      price = billboard.sellingRate?.monthly?.month1 > 0
        ? billboard.sellingRate.monthly.month1
        : billboard.currentPrice ?? 0;
    }

    const currency = billboard?.price?.localCurrencies
      ? billboard?.price?.localCurrencies?.code
      : billboard?.currencyCode;

    const cartItem: CartItem = {
      id: billboard.id,
      name: billboard.displayName || billboard.name,
      location: billboard.stateName || billboard.inventoryAddress,
      state: billboard.stateName || billboard.inventoryAddress,
      price: Number(price) * 100, // Convert to cents for cart context
      impressions: billboard.inventoryReports?.totalPotentialViews || billboard.monthlySummary?.totalVisitors || 0,
      uniqueVisitors: billboard.monthlySummary?.uniqueVisitors || 0,
      frequency: billboard.monthlySummary?.frequency || 0,
      mediaOwner: billboard.mediaOwnerName || 'N/A',
      size: (billboard.panelWidth && billboard.panelHeight
        ? `${billboard.panelWidth}x${billboard.panelHeight}`
        : 'Standard'),
      type: billboard.inventoryType || billboard.type || 'Classic', // Default to Classic
      status: billboard.availableBooking ? "available" : "sold-out",
      image: (billboard.fullResImagePath &&
        billboard.fullResImagePath !== 'null' &&
        billboard.fullResImagePath.trim() !== ''
        ? billboard.fullResImagePath
        : billboard.thumbnailPath && billboard.thumbnailPath !== 'null' && billboard.thumbnailPath.trim() !== ''
          ? billboard.thumbnailPath
          : billboard.imageUrl && billboard.imageUrl !== 'null' && billboard.imageUrl.trim() !== ''
            ? billboard.imageUrl
            : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`),
      currencyCode: currency || 'N/A',
      // Attach country information to billboard item
      countryId: selectedCountryId,
      countryName: countryOptions.find((c: any) => c.id === selectedCountryId)?.label || localStorage.getItem('sessionDefaultCountryName') || 'N/A',
      specifications: {
        resolution: (
          billboard.type === 'CLASSIC'
            ? 'N/A'
            : (billboard.resolutionWidth && billboard.resolutionHeight
              ? `${billboard.resolutionWidth}x${billboard.resolutionHeight}`
              : (billboard.specification?.resolution1Width && billboard.specification?.resolution1Height
                ? `${billboard.specification.resolution1Width}x${billboard.specification.resolution1Height}`
                : (billboard.sizeResolution)))
        ),
        duration: billboard.type?.toLowerCase() === "classic" ? "30 days" : "Variable"
      },
      priceFormat,
      isSpotBased: (priceFormat === 'spot-based') ? true : false,
      startDate: startDate,
      endDate: endDate,
    };

    // Store cart country when first item is added to cart OR if cart has items but no cartCountry set
    if (cartItems.length === 0 || (!cartCountry && cartItems.length > 0)) {
      const currentCountryObj = countryOptions.find((c: any) => c.id === selectedCountryId);

      if (currentCountryObj && cartCountryId !== currentCountryObj.id) {
        setCartCountry(currentCountryObj?.countryId.toLowerCase());
        setCartCountryId(currentCountryObj?.id);
        // Update tax information in cart context
        if (currentCountryObj.tax) {
          const taxLabel = currentCountryObj?.tax?.label || 'Tax';
          const taxPercent = currentCountryObj?.tax?.percent || 0;
          updateTaxInfo(taxLabel, taxPercent);
        } else {
          // Fallback tax info if country doesn't have tax data
          updateTaxInfo('Tax', 0);
        }
      } else {
        logger.info('❌ Could not find currentCountryObj for selectedCountryId:', selectedCountryId);
      }
    } else {
      logger.info('🛒 Cart country already set or no need to set:', cartCountry);
    }

    addToCart(cartItem);
    toast({
      title: "Added to cart",
      description: "Billboard has been added to your cart successfully.",
    });
  };

  const handleRemoveFromCart = (billboard: any) => {
    removeFromCart(billboard.id);
    toast({
      title: "Removed from cart",
      description: "Billboard has been removed from your cart.",
    });
  };

  const toggleSection = (section: keyof typeof defaultExpandedSections) => {
    setExpandedSections((prev: typeof expandedSections) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const updateFilter = (key: string, value: any) => {
    setFilters((prev: typeof filters) => ({
      ...prev,
      [key]: value,
      // Clear Environment and Media Owner when switching to packages
      ...(key === 'category' && value === 'packages' && {
        environment: '',
        mediaOwner: []
      })
    }));

    // // Trigger package API call when category changes to 'packages'
    // if (key === 'category' && value === 'packages') {
    //   setTimeout(() => {
    //     fetchPackageData(selectedCountryId, triggerSearch || searchQuery);
    //   }, 300);
    // }
  };

  const toggleArrayFilter = (key: string, value: string) => {
    setFilters((prev: typeof filters) => ({
      ...prev,
      [key]: (prev[key as keyof typeof prev] as string[]).includes(value)
        ? (prev[key as keyof typeof prev] as string[]).filter(item => item !== value)
        : [...(prev[key as keyof typeof prev] as string[]), value]
    }));
  };

  // New function for single value filters (like radio buttons for venue type)
  const handleSingleFilter = (key: string, value: string) => {

    setFilters((prev: typeof filters) => {
      const newFilters = {
        ...prev,
        [key]: value
      };

      if (typeof window !== 'undefined') {
        localStorage.setItem('explore-filters', JSON.stringify(newFilters));
      }

      // Update environment ref immediately for API calls
      if (key === 'environment') {
        currentEnvironmentRef.current = value;
      } else if (key === 'venueType') {

        const parentVenueType = value ? [value] : []; // Only include the parent value
        setVenueTypeData(parentVenueType);

        // const expandedVenueTypes: string[] = [];

        // if (value) {
        //   // Add the parent itself
        //   expandedVenueTypes.push(value);
        //   // Add all children and grandchildren
        //   const children = getAllChildrenForVenueType(value);
        //   expandedVenueTypes.push(...children);
        // }

        // // Remove duplicates and update venueTypeData
        // const uniqueVenueTypes = [...new Set(expandedVenueTypes)];
        // setVenueTypeData(uniqueVenueTypes);
      }

      return newFilters;
    });
  };

  // Handle state selection (single-select)
  const handleStateSelection = (stateId: string) => {
    setFilters((prev: any) => {
      const alreadySelected = prev.state.includes(stateId);
      const newStates = alreadySelected
        ? prev.state.filter((id: string) => id !== stateId)
        : [...prev.state, stateId];
      // Remove districts belonging to deselected state
      let newDistricts = prev.district;
      if (alreadySelected) {
        const districtsToRemove = availableDistricts
          .filter((d: any) => d.stateId === stateId || d.state_id === stateId)
          .map((d: any) => d.id || d.districtId);
        newDistricts = prev.district.filter((id: string) => !districtsToRemove.includes(id));
      }
      return {
        ...prev,
        state: newStates,
        district: newDistricts
      };
    });
    setAvailableDistricts([]);
    setShowStateDropdown(false);
  };

  // Handle district selection (single-select)
  const handleDistrictSelection = (districtId: string) => {
    setFilters((prev: any) => {
      const alreadySelected = prev.district.includes(districtId);
      const newDistricts = alreadySelected
        ? prev.district.filter((id: string) => id !== districtId)
        : [...prev.district, districtId];
      return {
        ...prev,
        district: newDistricts
      };
    });
    setShowDistrictDropdown(false);
  };

  // Handle country selection (single-select)
  const handleCountrySelection = (countryId: string) => {
    // 🚨 STRICT: If cart has ANY items, prevent country change
    if (hasCartCountry()) {
      const currentCartCountry = countryOptions.find((c: any) => c.id === cartCountryId);
      toast({
        title: "Cannot change country",
        description: `Your cart contains items from ${currentCartCountry?.label || 'current country'}. To change countries, please clear your cart first.`,
        variant: "destructive"
      });
      setShowCountryDropdown(false);
      return;
    }

    // Allow country change only when cart is empty
    const selectedCountry = countryOptions.find((c: any) => c.value === countryId || c.id === countryId);

    if (selectedCountry) {
      if (selectedCountry.id === selectedCountryId) {
        setShowCountryDropdown(false);
        return;
      }

      // Set selectedCountryId state
      setSelectedCountryId(selectedCountry.id);

      setFilters((prev: any) => {
        return {
          ...prev,
          country: selectedCountry.id,
          state: [], // Clear states when country changes
          district: [], // Clear districts when country changes
          mediaOwner: [],
          venueType: []
        };
      });

      // Update selectedCountryId in localStorage (active filter)
      localStorage.setItem('selectedCountryId', selectedCountry.id);
      localStorage.setItem('selectedCountryName', selectedCountry.label);

      // Update tax information in cart context
      if (selectedCountry.tax) {
        const taxLabel = selectedCountry.tax.label || 'Tax';
        const taxPercent = selectedCountry.tax.percent || 0;
        updateTaxInfo(taxLabel, taxPercent);
      } else {
        // Fallback tax info if country doesn't have tax data
        updateTaxInfo('Tax', 0);
      }

      // Reset pagination to first page
      setCurrentPage(0);
      localStorage.setItem('exploreCurrentPage', '0');

      // Clear available states and districts
      setAvailableStates([]);
      setAvailableDistricts([]);
    }

    setShowCountryDropdown(false);
  };
  const clearAllFilters = async () => {
    // 🔐 Check if cart has items - if yes, preserve current country
    const hasCartItems = cartItems.length > 0;

    let countryToUse = '';

    if (hasCartItems) {
      // 🛒 Cart has items: Keep current country (don't reset to default)
      countryToUse = selectedCountryId;
    } else {
      // 🆓 Cart is empty: Revert to sessionDefaultCountryId OR First Available
      const sessionDefault = localStorage.getItem('sessionDefaultCountryId');

      // Find the country object corresponding to session default
      // We check against both 'id' and 'countryId' to be safe
      let targetCountry = countryOptions.find((c: any) => c.id === sessionDefault || c.countryId === sessionDefault);

      // [FIX] Fallback: If no session default found (or invalid), use the first available country
      // This guarantees we don't get stuck on the "Selected" country if session default is missing
      if (!targetCountry && countryOptions.length > 0) {
        targetCountry = countryOptions[0];
      }

      if (targetCountry) {
        countryToUse = targetCountry.value; // Use the value ID used for state

        // 1. Update State immediately
        setSelectedCountryId(countryToUse);

        // 2. Clear "User Selection" overrides so it falls back to session default logic
        if (typeof window !== 'undefined') {
          localStorage.removeItem('selectedCountryId');
          localStorage.removeItem('selectedCountryName');
        }

        // 3. Update Tax Info
        if (targetCountry.tax) {
          updateTaxInfo(targetCountry.tax.label || 'Tax', targetCountry.tax.percent || 0);
        } else {
          updateTaxInfo('Tax', 0);
        }
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const urlType = urlParams.get('type');
    setFilters({
      ...defaultFilters,
      category: '', // Clear category (no packages)
      inventoryType: urlType || 'digital', // Preserve URL type or default to digital
      country: countryToUse, // ✅ Use preserved country if cart has items, or default country if cart is empty
      environment: "",
      spotBasedBookingOnly: false,
      state: [], // ✅ Explicitly clear states
      district: [] // ✅ Explicitly clear districts
    });

    // Reset environment ref
    currentEnvironmentRef.current = '';
    setSearchQuery("");
    setLocalPriceRange([0, 0]);
    setMinPriceInput('');
    setMaxPriceInput('');
    setVenueTypeData([]);

    // ✅ Clear states and districts immediately
    setAvailableStates([]);
    setAvailableDistricts([]);

    setTriggerSearch("");
    setBuyingFormat('day-based');
    setPriceFormat('day-based');

    if (typeof window !== 'undefined') {
      localStorage.removeItem('explore-filters');
      localStorage.removeItem('explore-search-query');

      // ✅ Only remove selectedCountryName if cart is empty
      if (!hasCartItems) {
        localStorage.removeItem('selectedCountryName');
        localStorage.removeItem('selectedCountryId');
      } else {
        logger.info('🛒 [Clear All] Preserving selectedCountryName due to cart items');
      }
    }

    // ✅ Refetch states for the country (preserved or default)
    if (countryToUse) {
      try {
        const { data } = await refetchStates({
          countryIds: countryToUse, // ✅ Use countryIds (plural) to match the query variable name
          exposure: exposure
        });

        if (data?.stateListMarketplace) {
          setAvailableStates(data.stateListMarketplace);
        } else {
          setAvailableStates([]);
        }
      } catch (error) {
        logger.error("❌ Error while refetching states:", error);
        setAvailableStates([]);
      }
    } else {
      setAvailableStates([]);
    }
  };


  // const getStatusColor = (inventoryItem: any) => {
  //   if (!inventoryItem.active) {
  //     return "bg-red-100 text-red-700";
  //   } else {
  //     return "bg-emerald-100 text-emerald-700";
  //   }
  // };

  // const getStatusText = (inventoryItem: any) => {
  //   if (!inventoryItem.active) {
  //     return "Sold Out";
  //   } else {
  //     return "Available";
  //   }
  // };

  // const handleViewDetails = (inventoryItem: any) => {
  //   // Get the billboard type from the inventory item
  //   const billboardType = inventoryItem.inventoryType || inventoryItem.type || 'digital';
  //   navigate(`${base_path}/billboard/${inventoryItem.id}?type=${encodeURIComponent(billboardType)}`, {
  //     state: {
  //       currentPrice: inventoryItem.currentPrice
  //     }
  //   });
  // };

  // Get page numbers for pagination (adapted from campaigns.tsx)
  const getPageNumbers = () => {
    const pagesCount = totalPages;
    if (pagesCount <= 5) {
      return Array.from({ length: pagesCount }, (_, i) => i);
    }

    const pages: (number | string)[] = [];

    // Always show first page
    pages.push(0);

    // Show dots if needed
    if (currentPage > 3) pages.push('...');

    // Show pages around current
    const start = Math.max(1, currentPage - 1);
    const end = Math.min(pagesCount - 2, currentPage + 1);
    for (let i = start; i <= end; i++) {
      if (i !== 0 && i !== pagesCount - 1) {
        pages.push(i);
      }
    }

    // Show dots if needed
    if (currentPage < pagesCount - 4) pages.push('...');

    // Always show last page
    if (pagesCount > 1) {
      pages.push(pagesCount - 1);
    }

    return pages;
  };

  const FilterSection = ({ title, isExpanded, onToggle, children }: any) => (
    <div className="border-b border-gray-200 pb-4 mb-4">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left"
      >
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </button>
      {isExpanded && <div className="mt-3 space-y-3">{children}</div>}
    </div>
  );

  const CheckboxGroup = ({ options, value, onChange }: any) => (
    <div className="space-y-2">
      {options.map((option: string) => (
        <div key={option} className="flex items-center space-x-2">
          <Checkbox
            id={option}
            checked={value.includes(option)}
            onCheckedChange={() => onChange(option)}
          />
          <Label htmlFor={option} className="text-sm text-gray-700 cursor-pointer">
            {option}
          </Label>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <HeroSection
        featuredBillboards={featuredBillboards}
        currencyCode={featuredBillboards.length > 0 ? (featuredBillboards[0]?.price?.localCurrencies ? featuredBillboards[0]?.price?.localCurrencies?.code : featuredBillboards[0]?.currencyCode || 'N/A') : currencyCode}
        onAddToCart={handleAddToCart}
        onRemoveFromCart={handleRemoveFromCart}
        isInCart={isInCart}
        handleInventorySelect={handleInventorySelect}
        filters={filters}
        isLoading={isPageLoading}
        noDataMessage={!isPageLoading && featuredBillboards.length === 0 ? noDataMessage : null}
      />

      {/* Country selection handled in landing page only */}

      <div className="max-w-[100rem] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Sidebar - Filters */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-emerald-600">Filter By</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-emerald-600 hover:text-emerald-700"
                  >
                    Clear All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Search */}
                <div>
                  <Label htmlFor="search" className="text-sm font-medium text-gray-900">
                    Search
                  </Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="search"
                      type="text"
                      placeholder="Search Inventories"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          // Only trigger search if query is empty or >= 3 characters
                          if (searchQuery.length === 0 || searchQuery.length >= 3) {
                            // Check if current category is packages
                            if (filters.category === 'packages') {
                              fetchPackageData(selectedCountryId, searchQuery);
                            } else {
                              // Regular inventory search
                              setTriggerSearch(searchQuery);
                            }
                            if (currentPage !== 0) {
                              setCurrentPage(0);
                            }
                          }
                        }
                      }}
                      className={`px-10 pr-10 ${searchQuery.length > 0 && searchQuery.length < 3
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500'
                        }`}
                    />
                    {searchQuery && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetSearch}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                        title="Clear search"
                      >
                        <X className="h-3 w-3 text-gray-400" />
                      </Button>
                    )}
                  </div>
                  {searchQuery.length > 0 && searchQuery.length < 3 && (
                    <p className="text-xs text-red-600 mt-1">
                      Enter at least 3 characters, then press Enter to search
                    </p>
                  )}
                </div>

                <Separator />

                {/* Location Filters */}
                <FilterSection
                  title="Location"
                  isExpanded={expandedSections.location}
                  onToggle={() => toggleSection('location')}
                >
                  {/* Country Dropdown */}
                  <div>
                    <div className="flex items-center space-x-1">
                      <Label className="text-sm font-medium text-gray-700">
                        Country
                      </Label>
                      {cartItems.length > 0 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Info className="h-3 w-3 text-gray-400 cursor-pointer" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-left bg-gray-900 text-white p-3 rounded-md shadow-lg border border-gray-700">
                              <div className="space-y-1">
                                <p className="text-gray-200">Remove all Inventories or start a new proposal to change country.</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className="relative mt-1" data-dropdown="country">
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (hasCartCountry()) {
                            toast({
                              title: "Cannot change country",
                              description: `You have already selected inventory from ${countryOptions.find((c: any) => c.value === filters.country)?.label || 'current country'}. To change countries, please remove all selected inventory or start a new proposal.`,
                              variant: "destructive"
                            });
                            return;
                          }
                          // Close other dropdowns when opening country dropdown
                          if (!showCountryDropdown) {
                            setShowStateDropdown(false);
                            setShowDistrictDropdown(false);
                            setCountrySearchTerm('');
                          } else {
                            setCountrySearchTerm('');
                          }
                          setShowCountryDropdown(!showCountryDropdown);
                        }}
                        className="w-full justify-between text-left font-normal"
                        disabled={cartItems.length > 0}
                      >
                        <span className="truncate">
                          {countryLoading
                            ? 'Loading countries...'
                            : !selectedCountryId
                              ? 'Select country'
                              : countryOptions.find((c: any) => c.id === selectedCountryId)?.label || 'Selected country'}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {showCountryDropdown && countryOptions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
                          {/* Search Input */}
                          <div className="p-2 border-b border-emerald-200 sticky top-0 bg-white ">
                            <div className="relative">
                              <Input
                                type="text"
                                placeholder="Search country..."
                                value={countrySearchTerm}
                                onChange={(e) => setCountrySearchTerm(e.target.value)}
                                className="h-8 text-sm border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500 pr-8"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                              {countrySearchTerm && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setCountrySearchTerm('')}
                                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                                  title="Clear search"
                                >
                                  <X className="h-3 w-3 text-gray-400" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Options List */}
                          <div className="overflow-y-auto">
                            {filteredCountryOptions.length > 0 ? (
                              filteredCountryOptions.map((country: any) => {
                                const isSelected = selectedCountryId === country.id;
                                return (
                                  <div
                                    key={country.value}
                                    className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2 ${isSelected ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                                    onClick={() => handleCountrySelection(country.value)}
                                  >
                                    <span className="text-sm flex-1">{country.label}</span>
                                    {isSelected && (
                                      <span className="text-emerald-600">✓</span>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                No countries found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Single-select State Dropdown */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      State
                    </Label>
                    <div className="relative mt-1" data-dropdown="state">
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Close other dropdowns when opening state dropdown
                          if (!showStateDropdown) {
                            setShowCountryDropdown(false);
                            setShowDistrictDropdown(false);
                            setStateSearchTerm('');
                          } else {
                            setStateSearchTerm('');
                          }
                          setShowStateDropdown(!showStateDropdown);
                        }}
                        className="w-full justify-between text-left font-normal"
                        disabled={stateLoading || !availableStates.length || !filters.country}
                      >
                        {/* --- State Dropdown Button Display --- */}
                        <span className="truncate">
                          {!filters.country
                            ? 'Select Country First'
                            : stateLoading
                              ? 'Loading States...'
                              : !availableStates.length && filters.country
                                ? 'No States Available'
                                : filters.state.length === 0
                                  ? 'Select State'
                                  : filters.state.length === 1
                                    ? (availableStates.find(s => (s.stateId || s.id) === filters.state[0])?.name ||
                                      availableStates.find(s => (s.stateId || s.id) === filters.state[0])?.stateName ||
                                      'Selected State')
                                    : `${filters.state.length} selected`}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {showStateDropdown && availableStates.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
                          {/* Search Input */}
                          <div className="p-2 border-b border-emerald-200 sticky top-0 bg-white">
                            <div className="relative">
                              <Input
                                type="text"
                                placeholder="Search state..."
                                value={stateSearchTerm}
                                onChange={(e) => setStateSearchTerm(e.target.value)}
                                className="h-8 text-sm border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500 pr-8"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                              {stateSearchTerm && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setStateSearchTerm('')}
                                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                                  title="Clear search"
                                >
                                  <X className="h-3 w-3 text-gray-400" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Options List */}
                          <div className="overflow-y-auto">
                            {/* Clear option - Only show if states are selected */}
                            {filters.state.length > 0 && (
                              <div
                                className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                                onClick={() => {
                                  setFilters((prev: any) => ({ ...prev, state: [], district: [] }));
                                  setAvailableDistricts([]);
                                  setShowStateDropdown(false);
                                  setStateSearchTerm('');
                                }}
                              >
                                <span className="text-sm text-gray-500">Clear selection</span>
                              </div>
                            )}
                            {/* --- State Dropdown List --- */}
                            {filteredStateOptions.length > 0 ? (
                              filteredStateOptions.map((state) => {
                                const stateId = state.stateId || state.id;
                                const stateName = state.name || state.stateName;
                                const isSelected = filters.state.includes(stateId);
                                return (
                                  <div
                                    key={stateId}
                                    className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2 ${isSelected ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                                    onClick={() => handleStateSelection(stateId)}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => { }} // Handled by parent onClick
                                      className="pointer-events-none !border-emerald-600 !data-[state=checked]:bg-emerald-600"
                                    />
                                    <span className="text-sm flex-1">{stateName}</span>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                No states found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Single-select District Dropdown */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      District
                    </Label>
                    <div className="relative mt-1" data-dropdown="district">
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Close other dropdowns when opening district dropdown
                          if (!showDistrictDropdown) {
                            setShowCountryDropdown(false);
                            setShowStateDropdown(false);
                            setDistrictSearchTerm('');
                          } else {
                            setDistrictSearchTerm('');
                          }
                          setShowDistrictDropdown(!showDistrictDropdown);
                        }}
                        className="w-full justify-between text-left font-normal"
                        disabled={districtLoading || !availableDistricts.length || !filters.state.length}
                      >
                        {/* --- District Dropdown Button Display --- */}
                        <span className="truncate">
                          {!filters.state.length
                            ? 'Select State First'
                            : districtLoading
                              ? 'Loading districts...'
                              : !availableDistricts.length && filters.state.length > 0
                                ? 'No districts available'
                                : filters.district.length === 0
                                  ? 'Select district'
                                  : filters.district.length === 1
                                    ? (availableDistricts.find(d => (d.id || d.districtId) === filters.district[0])?.name ||
                                      availableDistricts.find(d => (d.id || d.districtId) === filters.district[0])?.districtName ||
                                      'Selected district')
                                    : `${filters.district.length} selected`}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {showDistrictDropdown && availableDistricts.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
                          {/* Search Input */}
                          <div className="p-2 border-b border-emerald-200 sticky top-0 bg-white">
                            <div className="relative">
                              <Input
                                type="text"
                                placeholder="Search district..."
                                value={districtSearchTerm}
                                onChange={(e) => setDistrictSearchTerm(e.target.value)}
                                className="h-8 text-sm border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500 pr-8"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                              {districtSearchTerm && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDistrictSearchTerm('')}
                                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                                  title="Clear search"
                                >
                                  <X className="h-3 w-3 text-gray-400" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Options List */}
                          <div className="overflow-y-auto">
                            {/* Clear option - Only show if districts are selected */}
                            {filters.district.length > 0 && (
                              <div
                                className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                                onClick={() => {
                                  setFilters((prev: any) => ({ ...prev, district: [] }));
                                  setShowDistrictDropdown(false);
                                  setDistrictSearchTerm('');
                                }}
                              >
                                <span className="text-sm text-gray-500">Clear selection</span>
                              </div>
                            )}
                            {/* --- District Dropdown List --- */}
                            {filteredDistrictOptions.length > 0 ? (
                              filteredDistrictOptions.map((district) => {
                                const districtId = district.id || district.districtId;
                                const districtName = district.name || district.districtName;
                                const isSelected = filters.district.includes(districtId);
                                return (
                                  <div
                                    key={districtId}
                                    className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2 ${isSelected ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                                    onClick={() => handleDistrictSelection(districtId)}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => { }} // Handled by parent onClick
                                      className="pointer-events-none !border-emerald-600 !data-[state=checked]:bg-emerald-600"
                                    />
                                    <span className="text-sm flex-1">{districtName}</span>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                No districts found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </FilterSection>

                {/* Category Filters */}
                <FilterSection
                  title="Inventory Type"
                  isExpanded={expandedSections.category}
                  onToggle={() => toggleSection('category')}
                >
                  <div className="space-y-2">
                    {['digital', 'classic'].map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id={type}
                          name="inventoryTypeRadio"
                          checked={filters.inventoryType === type && filters.category !== 'packages'}
                          onChange={() => {
                            updateFilter('inventoryType', type);
                            updateFilter('category', ''); // Clear category when selecting inventory type
                            if (type === 'digital') {
                              // updateFilter('spotBasedBookingOnly', true);
                              setPriceFormat('day-based');
                            }
                          }}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor={type} className="text-sm text-gray-700 cursor-pointer capitalize">
                          {type}
                        </Label>
                      </div>
                    ))}
                    {/* <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="packages"
                        name="inventoryTypeRadio"
                        checked={filters.category === 'packages'}
                        onChange={() => {
                          updateFilter('category', 'packages');
                        }}
                        className="form-radio h-4 w-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                      />
                      <Label htmlFor="packages" className="text-sm text-gray-700 cursor-pointer capitalize">
                        packages
                      </Label>
                    </div> */}
                  </div>
                </FilterSection>

                {/* Buying Format Filter - Only for Digital */}
                {filters.inventoryType === 'digital' && filters.category !== 'packages' && (
                  <FilterSection
                    title="Buying Format"
                    isExpanded={expandedSections.buyingFormat}
                    onToggle={() => toggleSection('buyingFormat')}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="spot-based"
                          name="buyingFormatRadio"
                          checked={buyingFormat === 'spot-based'}
                          onChange={() => {
                            setBuyingFormat('spot-based');
                            setPriceFormat('spot-based');
                            setCurrentPage(0);
                            setSpotBasedBooking(true);
                          }}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="spot-based" className="text-sm text-gray-700 cursor-pointer capitalize">
                          Spot based
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="day-based"
                          name="buyingFormatRadio"
                          checked={buyingFormat === 'day-based'}
                          onChange={() => {
                            setBuyingFormat('day-based');
                            setPriceFormat('day-based');
                            setCurrentPage(0);
                            setSpotBasedBooking(false);
                          }}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="day-based" className="text-sm text-gray-700 cursor-pointer capitalize">
                          Day based
                        </Label>
                      </div>
                    </div>
                  </FilterSection>
                )}

                {/* Value Packs Filters */}
                <FilterSection
                  title="Value Packs"
                  isExpanded={expandedSections.valuePacks}
                  onToggle={() => toggleSection('valuePacks')}
                >
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="packages"
                        name="inventoryTypeRadio"
                        checked={filters.category === 'packages'}
                        onChange={() => {
                          updateFilter('category', 'packages');
                        }}
                        className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                      />
                      <Label htmlFor="packages" className="text-sm text-gray-700 cursor-pointer capitalize">
                        packages
                      </Label>
                    </div>
                  </div>
                </FilterSection>

                {/* Environment Filters - Hidden when packages category is selected */}
                {filters.category !== 'packages' && (
                  <FilterSection
                    title="Environment"
                    isExpanded={expandedSections.environment}
                    onToggle={() => toggleSection('environment')}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="environment-all-explore"
                          name="environmentRadio"
                          checked={filters.environment === '' || !filters.environment}
                          onChange={() => handleSingleFilter('environment', '')}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="environment-all-explore" className="text-sm text-gray-700 cursor-pointer">
                          All
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="indoor-explore"
                          name="environmentRadio"
                          checked={filters.environment === 'INDOOR'}
                          onChange={() => handleSingleFilter('environment', 'INDOOR')}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="indoor-explore" className="text-sm text-gray-700 cursor-pointer">
                          Indoor
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="outdoor-explore"
                          name="environmentRadio"
                          checked={filters.environment === 'OUTDOOR'}
                          onChange={() => handleSingleFilter('environment', 'OUTDOOR')}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="outdoor-explore" className="text-sm text-gray-700 cursor-pointer">
                          Outdoor
                        </Label>
                      </div>
                    </div>
                  </FilterSection>
                )}
                {/* Pricing Filter */}
                {/* <FilterSection
                  title="Pricing"
                  isExpanded={expandedSections.pricing}
                  onToggle={() => toggleSection('pricing')}
                >
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-700">Min Price</Label>
                        <Input
                          type="text"
                          placeholder="0"
                          value={minPriceInput}
                          onChange={handleMinPriceChange}
                          onKeyPress={handlePriceKeyPress}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-700">Max Price</Label>
                        <Input
                          type="text"
                          placeholder="No limit"
                          value={maxPriceInput}
                          onChange={handleMaxPriceChange}
                          onKeyPress={handlePriceKeyPress}
                          className="text-sm"
                        />
                      </div>
                    </div>
                    <Button 
                      onClick={handlePriceRangeUpdate}
                      size="sm"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    >
                      Apply Price Range
                    </Button>
                    <div className="text-xs text-gray-500 text-center">
                      Press Enter in fields or click Apply
                    </div>
                  </div>
                </FilterSection> */}

                {/* Media Owner */}
                {filters.category !== 'packages' && (
                  <FilterSection
                    title="Media Owner"
                    isExpanded={expandedSections.mediaOwner}
                    onToggle={() => toggleSection('mediaOwner')}
                  >
                    <div className="relative" data-dropdown="mediaOwner">
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Close other dropdowns when opening media owner dropdown
                          if (!showMediaOwnerDropdown) {
                            setShowCountryDropdown(false);
                            setShowStateDropdown(false);
                            setShowDistrictDropdown(false);
                            setMediaOwnerSearchTerm('');
                          } else {
                            setMediaOwnerSearchTerm('');
                          }
                          setShowMediaOwnerDropdown(!showMediaOwnerDropdown);
                        }}
                        className="w-full justify-between text-left font-normal"
                        disabled={mediaOwnerLoading || mediaOwnerOptions.length === 0}
                      >
                        <span className="truncate">
                          {mediaOwnerLoading
                            ? 'Loading media owners...'
                            : mediaOwnerOptions.length === 0
                              ? 'No media owners available'
                              : filters.mediaOwner?.length === 0
                                ? 'Select Media Owner'
                                : filters.mediaOwner?.length === 1
                                  ? mediaOwnerOptions.find((o: any) => o.value === filters.mediaOwner?.[0])?.label || 'Selected'
                                  : `${filters.mediaOwner?.length} selected`}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {showMediaOwnerDropdown && mediaOwnerOptions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
                          {/* Search Input */}
                          <div className="p-2 border-b border-emerald-200 sticky top-0 bg-white">
                            <div className="relative">
                              <Input
                                type="text"
                                placeholder="Search media owner..."
                                value={mediaOwnerSearchTerm}
                                onChange={(e) => setMediaOwnerSearchTerm(e.target.value)}
                                className="h-8 text-sm border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500 pr-8"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                              {mediaOwnerSearchTerm && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setMediaOwnerSearchTerm('')}
                                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                                  title="Clear search"
                                >
                                  <X className="h-3 w-3 text-gray-400" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Options List */}
                          <div className="overflow-y-auto">
                            {/* Clear option */}
                            <div
                              className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={() => {
                                setFilters((prev: any) => ({ ...prev, mediaOwner: [] }));
                                setShowMediaOwnerDropdown(false);
                                setMediaOwnerSearchTerm('');
                              }}
                            >
                              <span className="text-sm text-gray-500">Clear selection</span>
                            </div>
                            {/* --- Media Owner Dropdown List --- */}
                            {filteredMediaOwnerOptions.length > 0 ? (
                              filteredMediaOwnerOptions.map((owner: any) => {
                                const isSelected = filters.mediaOwner?.includes(owner.value) || false;
                                return (
                                  <div
                                    key={owner.value}
                                    className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2 ${isSelected ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                                    onClick={() => {
                                      toggleArrayFilter('mediaOwner', owner.value);
                                    }}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => { }} // Handled by parent onClick
                                      className="pointer-events-none !border-emerald-600 !data-[state=checked]:bg-emerald-600"
                                    />
                                    <span className="text-sm flex-1">{owner.label}</span>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                No media owners found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </FilterSection>
                )}

                {/* Venue Type */}
                {filters.category !== 'packages' && (
                  <FilterSection
                    title="Venue Type"
                    isExpanded={expandedSections.venue}
                    onToggle={() => toggleSection('venue')}
                  >
                    <div className="space-y-2">
                      <RadioGroup
                        value={filters.venueType || ""}
                        onValueChange={(value) => handleSingleFilter('venueType', value)}
                      >
                        {venueTypesHierarchy.map((venueType) => (
                          <div key={venueType.id} className="flex items-center space-x-2">
                            <RadioGroupItem
                              id={venueType.name}
                              value={venueType.value}
                              className="!border-emerald-600 !text-emerald-600"
                            />
                            <Label htmlFor={venueType.name} className="text-sm text-gray-700 cursor-pointer">
                              {venueType.value}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  </FilterSection>
                )}


                {/* Audience - Commented out for now */}
                {/* <FilterSection
                  title="Target Audience"
                  isExpanded={expandedSections.audience}
                  onToggle={() => toggleSection('audience')}
                >
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Age Range</Label>
                      <CheckboxGroup
                        options={['18-24', '25-34', '35-44', '45-54', '55+']}
                        value={filters.targetAgeRange || []}
                        onChange={(option: string) => toggleArrayFilter('targetAgeRange', option)}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Gender</Label>
                      <CheckboxGroup
                        options={['Male', 'Female', 'All']}
                        value={filters.targetGender || []}
                        onChange={(option: string) => toggleArrayFilter('targetGender', option)}
                      />
                    </div>
                  </div>
                </FilterSection> */}

                {/* Inventory Specification - Commented out for now */}
                {/* <FilterSection
                  title="Inventory Specification"
                  isExpanded={expandedSections.inventorySpec}
                  onToggle={() => toggleSection('inventorySpec')}
                >
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Screen Size</Label>
                      <CheckboxGroup
                        options={['Small (14x8)', 'Medium (20x10)', 'Large (30x15)', 'Extra Large (40x20)']}
                        value={filters.screenSize || []}
                        onChange={(option: string) => toggleArrayFilter('screenSize', option)}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Orientation</Label>
                      <CheckboxGroup
                        options={['Landscape', 'Portrait', 'Square']}
                        value={filters.orientation || []}
                        onChange={(option: string) => toggleArrayFilter('orientation', option)}
                      />
                    </div>
                  </div>
                </FilterSection> */}
              </CardContent>
            </Card>
          </div>

          {/* Right Content Area */}
          <div className="flex-1">
            {/* Top Bar with View Controls */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-emerald-600">{filters.category === 'packages' ? 'Explore Packages' : 'Explore Inventories'}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {isPageLoading
                      ? filters.category === 'packages' ? 'Loading Packages...' : 'Loading inventories...'
                      : paginatedInventory.length > 0
                        ? `Showing ${paginatedInventory.length} ${filters.category === 'packages' ? (paginatedInventory.length === 1 ? 'Package' : 'Packages') : (paginatedInventory.length === 1 ? 'Inventory' : 'Inventories')}`
                        : filters.category === 'packages'
                          ? packageData.length > 0
                            ? `Showing ${packageData.length} ${packageData.length === 1 ? 'Package' : 'Packages'}`
                            : 'No Packages Available.'
                          : inventory.length > 0
                            ? `Showing ${inventory.length} ${inventory.length === 1 ? 'Inventory' : 'Inventories'}`
                            : 'No data available'} {searchQuery && `for "${searchQuery}"`}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center border border-gray-200 rounded-lg p-1">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      className={`h-8 w-8 p-0 ${viewMode === 'grid'
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'hover:bg-emerald-50 hover:text-emerald-700'
                        }`}
                    >
                      <Grid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      className={`h-8 w-8 p-0 ${viewMode === 'list'
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'hover:bg-emerald-50 hover:text-emerald-700'
                        }`}
                    >
                      <List className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'map' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('map')}
                      className={`h-8 w-8 p-0 ${viewMode === 'map'
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'hover:bg-emerald-50 hover:text-emerald-700'
                        }`}
                    >
                      <Map className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            {isPageLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-600">
                  {filters.category === 'packages' ? 'Loading packages...' : 'Loading inventories...'}
                </p>
              </div>
            ) : viewMode === 'map' ? (
              <MapView
                inventory={filters.category === 'packages' ? paginatedInventory.map((pkg: any) => ({
                  ...pkg,
                  // Required fields for map markers
                  latitude: pkg.inventories?.[0]?.latitude,
                  longitude: pkg.inventories?.[0]?.longitude,
                  displayName: pkg.name,
                  stateName: pkg.country?.name,
                  availableBooking: pkg.numberOfPurchase < pkg.purchaseLimitation,
                  sellingRate: {
                    monthly: {
                      month1: pkg.totalPrice
                    }
                  },
                  isInCart: isInCart(pkg.id),
                  currency: pkg.currency || currencyCode,
                })) : inventory}
                onInventorySelect={handleInventorySelect}
                onAddToCart={handleAddToCart}
                onDeleteFromCart={handleRemoveFromCart}
                isInCart={isInCart}
                currencyCode={currencyCode}
                isViewingPackages={filters.category === 'packages'}
              />
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {paginatedInventory.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {filters.category === 'packages' ? 'No Packages Found' : 'No Inventory Found'}
                    </h3>
                    <p className="text-gray-600">Try adjusting your search or filters.</p>
                  </div>
                ) : (
                  paginatedInventory.map((inventoryItem: any) => {
                    if (filters.category === 'deals') {
                      return (
                        <DealCard
                          key={inventoryItem.id}
                          deal={inventoryItem}
                        />
                      );
                    } else if (filters.category === 'packages') {
                      logger.log("selectedcountryId inside package--->", selectedCountryId);
                      const currentCountryOption = countryOptions.find((c: any) => c.id === selectedCountryId);
                      const packageWithContext = {
                        ...inventoryItem,
                        // 1. Force root level country ID
                        countryId: selectedCountryId,
                        // 2. Force root level country object
                        country: {
                          ...(inventoryItem.country || {}),
                          id: selectedCountryId,
                          name: currentCountryOption?.label || inventoryItem.country?.name || countryName
                        },
                        // 3. Force country inside nested inventories (Crucial for packages)
                        inventories: inventoryItem.inventories?.map((inv: any) => ({
                          ...inv,
                          countryId: selectedCountryId,
                          country: {
                            ...(inv.country || {}),
                            id: selectedCountryId,
                            name: currentCountryOption?.label || inv.country?.name || countryName
                          },
                          // Ensure state/location names don't conflict if used for validation
                          stateName: inv.stateName || currentCountryOption?.label
                        })) || []
                      };
                      return (
                        <PackageCard
                          key={inventoryItem.id}
                          viewMode={viewMode}
                          package={[packageWithContext]} // Pass as array since PackageCard expects an array
                          useLocalCart={true} // Use local cart for explore page
                          currencyCode={inventoryItem?.price ? inventoryItem?.price?.localCurrencies?.code : inventoryItem?.currencyCode || 'N/A'}
                        />
                      );
                    } else {
                      return (
                        <BillboardCard
                          billboard={inventoryItem}
                          currencyCode={inventoryItem?.price ? inventoryItem?.price?.localCurrencies?.code : inventoryItem?.currencyCode || 'N/A'}
                          handleInventorySelect={handleInventorySelect}
                          handleAddToCart={handleAddToCart}
                          handleRemoveFromCart={handleRemoveFromCart}
                          isInCart={isInCart(inventoryItem.id)}
                          countryOptions={countryOptions}
                          selectedCountryId={filters.country}
                        />
                      );
                    }
                  })
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {paginatedInventory.length === 0 ? (
                  <div className="text-center py-12">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {filters.category === 'packages' ? 'No Packages Found' : 'No Inventory Found'}
                    </h3>
                    <p className="text-gray-600">Try adjusting your search or filters.</p>
                  </div>
                ) : (
                  paginatedInventory.map((inventoryItem: any) => {
                    if (filters.category === 'deals') {
                      return (
                        <DealCard
                          key={inventoryItem.id}
                          deal={inventoryItem}
                        />
                      );
                    } else if (inventoryItem.inventoryType === "Package" || filters.category === 'packages') {
                      logger.log("selectedcountryId inside package--->", selectedCountryId);
                      const currentCountryOption = countryOptions.find((c: any) => c.id === selectedCountryId);
                      const packageWithContext = {
                        ...inventoryItem,
                        countryId: selectedCountryId,
                        country: {
                          ...(inventoryItem.country || {}),
                          id: selectedCountryId,
                          name: currentCountryOption?.label || inventoryItem.country?.name || countryName
                        },
                        inventories: inventoryItem.inventories?.map((inv: any) => ({
                          ...inv,
                          countryId: selectedCountryId,
                          country: {
                            ...(inv.country || {}),
                            id: selectedCountryId,
                            name: currentCountryOption?.label || inv.country?.name || countryName
                          }
                        })) || []
                      };
                      return (
                        <PackageCard
                          key={inventoryItem.id}
                          viewMode={viewMode}
                          package={[packageWithContext]} // Pass as array since PackageCard expects an array
                          useLocalCart={true} // Use local cart for explore page
                          currencyCode={inventoryItem?.price ? inventoryItem?.price?.localCurrencies?.code : inventoryItem?.currencyCode || 'N/A'}
                        />
                      );
                    }

                    return (
                      <Card key={inventoryItem.id} className="overflow-hidden hover:shadow-md transition-shadow">
                        <CardContent className="p-4 sm:p-6">
                          <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                            {/* Image */}
                            <div className="relative flex-shrink-0 w-full sm:w-48">
                              <img
                                src={!inventoryItem.fullResImagePath || inventoryItem.fullResImagePath === "null"
                                  ? `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`
                                  : inventoryItem.fullResImagePath}
                                alt={inventoryItem.displayName || inventoryItem.name || inventoryItem.title}
                                className="w-full h-32 object-cover rounded-lg"
                              />
                              {isInCart(inventoryItem.id) && (
                                <Badge className="absolute top-2 right-2 bg-emerald-600 text-white">
                                  In Cart
                                </Badge>
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                    {inventoryItem.displayName || inventoryItem.name || inventoryItem.title}
                                  </h3>
                                  <p className="text-gray-600 mb-1">
                                    {inventoryItem.trDistrictName && inventoryItem.trStateName
                                      ? `${inventoryItem.trDistrictName}, ${inventoryItem.trStateName}`
                                      : inventoryItem.location || inventoryItem.state || 'Location not specified'}
                                  </p>
                                  <span className='text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-2 inline-block'>
                                    by {getMediaOwner(inventoryItem.mediaOwnerName)?.name || inventoryItem.mediaOwnerName || 'N/A'}
                                  </span>


                                  {/* <Badge className={getStatusColor(inventoryItem)}>
                                    {getStatusText(inventoryItem)}
                                  </Badge> */}
                                </div>

                                <div className="text-right ml-4">
                                  <div className="text-2xl font-bold text-emerald-600">
                                    {(() => {
                                      let price = 0;
                                      let suffix = '';

                                      if (inventoryItem.type === 'DIGITAL') {
                                        price = priceFormat === 'spot-based'
                                          ? inventoryItem.perSpotPrice
                                          : inventoryItem.perDayPrice;
                                        suffix = priceFormat === 'spot-based' ? '/Spot' : '/Day';
                                      } else {
                                        price = (inventoryItem?.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                          ? inventoryItem.sellingRate.monthly.month1
                                          : inventoryItem.currentPrice;
                                        suffix = '/Month';
                                      }

                                      if (!price || price === 0) return 'N/A';

                                      const displayCurrencyCode = inventoryItem?.price?.localCurrencies?.code || inventoryItem?.currencyCode || currencyCode || 'N/A';

                                      return `${displayCurrencyCode} ${Number(price).toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      })}${suffix}`;
                                    })()}
                                  </div>
                                </div>
                              </div>
                              {isAuthenticated ? (
                                <div className="flex items-center justify-between w-full text-sm text-gray-500 mb-3">
                                  <div className="flex items-center">
                                    <Monitor className="mr-1 h-4 w-4" />
                                    <span>
                                      {inventoryItem.type === 'CLASSIC'
                                        ? (inventoryItem.panelWidth && inventoryItem.panelHeight
                                          ? `${inventoryItem.panelWidth}x${inventoryItem.panelHeight}`
                                          : 'Standard')
                                        : (inventoryItem.resolutionWidth && inventoryItem.resolutionHeight
                                          ? `${inventoryItem.resolutionWidth}x${inventoryItem.resolutionHeight}`
                                          : inventoryItem.specification?.resolution1Width && inventoryItem.specification?.resolution1Height
                                            ? `${inventoryItem.specification.resolution1Width}x${inventoryItem.specification.resolution1Height}`
                                            : inventoryItem.sizeResolution || inventoryItem.size || 'Standard')}
                                    </span>
                                  </div>
                                  <div className="flex items-center">
                                    <Users className="mr-1 h-4 w-4" />
                                    <span>
                                      {formatNumber(inventoryItem.monthlySummary?.totalVisitors || inventoryItem.impressions || 0)} impressions
                                    </span>
                                  </div>
                                  <div className="flex items-center">
                                    <Calendar className="mr-1 h-4 w-4" />
                                    <span>
                                      {inventoryItem.type === 'DIGITAL' || inventoryItem.inventoryType === 'digital' ? '15s' : 'Monthly'}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                                  <div className="flex items-center justify-center text-xs text-gray-600">
                                    <Lock className="mr-2 h-3 w-3" />
                                    <span>Login to view full audience and performance data</span>
                                  </div>
                                </div>
                              )}
                              <div className="flex space-x-3 mt-4 justify-end gap-2 w-1/2 ml-auto">
                                {(() => {
                                  // Calculate price for this item
                                  let itemPrice = 0;
                                  if (inventoryItem.type === 'DIGITAL') {
                                    itemPrice = priceFormat === 'spot-based'
                                      ? inventoryItem.perSpotPrice
                                      : inventoryItem.perDayPrice;
                                  } else {
                                    itemPrice = (inventoryItem?.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                      ? inventoryItem.sellingRate.monthly.month1
                                      : inventoryItem.currentPrice;
                                  }
                                  const isPricedOut = !itemPrice || itemPrice === 0;

                                  return (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleInventorySelect(inventoryItem)}
                                        className="flex-1"
                                      >
                                        View Details
                                      </Button>
                                      {isInCart(inventoryItem.id) ? (
                                        <Button
                                          onClick={() => handleRemoveFromCart(inventoryItem)}
                                          size="sm"
                                          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                        >
                                          Remove
                                        </Button>
                                      ) : (
                                        <Button
                                          onClick={() => handleAddToCart(inventoryItem)}
                                          size="sm"
                                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                                          disabled={isPricedOut || !inventoryItem.active}
                                        >
                                          {isPricedOut ? 'Unavailable' : !inventoryItem.active ? 'Sold Out' : 'Add to Cart'}
                                        </Button>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            )}

            {/* Pagination */}
            {(filters.category === 'packages' ? packageData.length > 0 : inventory.length > 0) && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t mt-6 rounded-lg">
                <div className="flex items-center text-sm text-gray-700">
                  <span>
                    {filters.category === 'packages'
                      ? `Showing ${pageStartData} - ${pageEndData} of ${totalDataLength} ${totalDataLength === 1 ? 'Package' : 'Packages'}`
                      : `Showing ${pageStartData} - ${pageEndData} of ${totalDataLength} ${totalDataLength === 1 ? 'Inventory' : 'Inventories'}`}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={paginationMinus}
                    disabled={currentPage === 0}
                    className="flex items-center space-x-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Previous</span>
                  </Button>

                  <div className="flex items-center space-x-1">
                    {getPageNumbers().map((page, index) => (
                      <div key={index}>
                        {page === '...'
                          ? <span className="px-2 text-gray-500">...</span>
                          : (
                            <Button
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              onClick={() => handlePageClick(page as number)}
                              className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}
                            >
                              {(page as number) + 1}
                            </Button>
                          )}
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={paginationPlus}
                    disabled={!hasNextPage}
                    className="flex items-center space-x-1"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}